import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Building2, Users, Target, ClipboardList, Bell, Calculator, PieChart,
  FileText, Contact as ContactIcon, Image as ImageIcon, Settings2, ShieldCheck, Loader2, Handshake,
  TrendingUp as TrendingUpIcon, Flame, Clock, Trophy, Plus, Phone, Mail, Camera, Upload,
  Pencil, Check, X, ChevronDown, Home as HomeIcon, BarChart3, Search as SearchIcon, Trash2,
} from 'lucide-react';
import {
  CxRoot, CxLogoBox, CxBack, CxTile, CxTabs, CxAccordion, CxKV, CxSearch, CxItem, CxEmpty, CxBadge,
} from '../capex/ui';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TILES = [
  { key: 'clients', label: 'Compte Clients', Icon: Users, color: '#5b9bd5' },
  { key: 'prospects', label: 'Compte Prospects', Icon: Target, color: '#e0563b' },
  { key: 'opportunites', label: 'Opportunités', Icon: TrendingUpIcon, color: '#7dc242' },
  { key: 'cr', label: 'Comptes Rendus', Icon: ClipboardList, color: '#5b9bd5' },
  { key: 'actions', label: 'Actions à Mener', Icon: Bell, color: '#ef7623', badge: 20 },
  { key: 'etudes', label: 'Mes études CAPEX', Icon: Calculator, color: '#e0b45a' },
  { key: 'chiffre', label: 'Suivi du Chiffre', Icon: PieChart, color: '#8f6fd0' },
  { key: 'contrats', label: 'Contrats', Icon: FileText, color: '#8f7fe0' },
  { key: 'contact', label: 'Contact', Icon: ContactIcon, color: '#7a6fe0' },
  { key: 'photos', label: 'Photos', Icon: ImageIcon, color: '#7dc242' },
  { key: 'parametres', label: 'Paramètres', Icon: Settings2, color: '#ef7623', wide: true },
];

const ACCOUNT_INFO_FIELDS = [
  ['enseigne', "Nom de l'enseigne GMS"], ['name', 'Nom du compte'], ['owner_name', 'Propriétaire du compte'],
  ['address', 'Adresse (ligne 1)'], ['cp', 'Code postal'], ['city', 'Ville'], ['phone', 'Téléphone'],
];

function initials(name) { return (name || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase(); }
function fmtNum(v) { return new Intl.NumberFormat('fr-FR').format(v); }
function fmtK(v) { return (v || v === 0) ? Math.round(v / 1000) + ' K€' : '—'; }
function fmtVal(f, v) { if (v === null || v === undefined || v === '') return '—'; if (f.type === 'checkbox') return v ? 'Oui ✓' : 'Non'; if (f.type === 'number') return fmtNum(v); return String(v); }
function canConfigCs(user) { return ['super_admin', 'admin', 'directeur_commercial'].includes(user?.role) || (user?.email || '').toLowerCase() === 'c.perez@wasabicorner.fr'; }

const CS_VALID_SCREENS = ['home', 'clients', 'prospects', 'opportunites', 'cr', 'contrats', 'chiffre', 'actions', 'photos', 'etudes', 'contact', 'parametres', 'import', 'doublons', 'audit', 'rubriques', 'cr-listes'];

export default function CapexSalesPage() {
  const { api, token, user } = useAuth();
  const navigate = useNavigate();
  const [access, setAccess] = useState(null); // null = chargement, true = autorisé, false = refusé
  const [screen, setScreen] = useState(() => {
    try {
      const s = new URLSearchParams(window.location.search).get('screen');
      return CS_VALID_SCREENS.includes(s) ? s : 'home';
    } catch { return 'home'; }
  });
  const [wide, setWide] = useState(() => localStorage.getItem('cs_wide') === '1');
  const toggleWide = () => { const n = !wide; setWide(n); localStorage.setItem('cs_wide', n ? '1' : '0'); };
  useEffect(() => {
    let cancelled = false;
    api().get('/capex-sales/access').then(() => { if (!cancelled) setAccess(true); }).catch(() => { if (!cancelled) setAccess(false); });
    return () => { cancelled = true; };
  }, [api]);

  if (access === null) return (
    <AppLayout><CxRoot data-testid="capex-sales-loading"><div className="flex flex-col items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent)' }} /></div></CxRoot></AppLayout>
  );
  if (access === false) return (
    <AppLayout><CxRoot data-testid="capex-sales-denied"><div className="flex flex-col items-center justify-center py-24 text-center">
      <ShieldCheck className="w-12 h-12 cx-muted-3 mb-4" /><h1 className="cx-h1">Accès restreint</h1>
      <p className="cx-muted mt-1">Capex Sales n'est pas accessible avec ce compte. Contactez un administrateur.</p></div></CxRoot></AppLayout>
  );

  return (
    <AppLayout>
      <CxRoot data-testid="capex-sales-app">
        <div className="mx-auto transition-all duration-300" style={{ maxWidth: wide ? 1120 : 560 }}>
          <div className="flex justify-between items-center gap-2 mb-2 flex-wrap">
            {screen !== 'home' ? (
              <button onClick={() => setScreen('home')} className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg font-semibold" style={{ background: 'var(--accent)', color: '#fff' }} data-testid="cs-home-btn" title="Revenir à l'accueil Capex Sales">
                <HomeIcon className="w-4 h-4" /> Accueil
              </button>
            ) : <span />}
            <button onClick={toggleWide} className="text-xs inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--ink-3)' }} data-testid="cs-width-toggle">
              {wide ? '↔ Rétrécir (mobile)' : '↔ Élargir (grand écran)'}
            </button>
          </div>
          {screen === 'home' && <Home onOpen={setScreen} user={user} onOpenEtudes={() => navigate('/simulations/tableau')} />}
          {screen === 'clients' && <CompteAccounts key="clients" api={api} user={user} kind="Client" onBack={() => setScreen('home')} />}
          {screen === 'prospects' && <CompteAccounts key="prospects" api={api} user={user} kind="Prospect" onBack={() => setScreen('home')} />}
          {screen === 'opportunites' && <Opportunites api={api} user={user} onBack={() => setScreen('home')} />}
          {screen === 'cr' && <ComptesRendus api={api} user={user} onBack={() => setScreen('home')} />}
          {screen === 'contrats' && <Contrats api={api} user={user} onBack={() => setScreen('home')} />}
          {screen === 'chiffre' && <SuiviChiffre api={api} user={user} onBack={() => setScreen('home')} />}
          {screen === 'actions' && <ActionsMener api={api} user={user} onBack={() => setScreen('home')} />}
          {screen === 'photos' && <Photos api={api} token={token} onBack={() => setScreen('home')} />}
          {screen === 'etudes' && <EtudesCapex api={api} onBack={() => setScreen('home')} />}
          {screen === 'contact' && <ContactAnnuaire api={api} onBack={() => setScreen('home')} />}
          {screen === 'parametres' && <Parametres user={user} onBack={() => setScreen('home')} onNav={setScreen} />}
          {screen === 'import' && <ImportAdmin api={api} onBack={() => setScreen('parametres')} />}
          {screen === 'doublons' && <Doublons api={api} onBack={() => setScreen('parametres')} />}
          {screen === 'audit' && <AuditLog api={api} onBack={() => setScreen('parametres')} />}
          {screen === 'rubriques' && <OrdreRubriques api={api} user={user} onBack={() => setScreen('parametres')} />}
          {screen === 'cr-listes' && <CRDropdownsAdmin api={api} onBack={() => setScreen('parametres')} />}
          {!['home', 'clients', 'prospects', 'opportunites', 'cr', 'contrats', 'chiffre', 'actions', 'photos', 'etudes', 'contact', 'parametres', 'import', 'doublons', 'audit', 'rubriques', 'cr-listes'].includes(screen) && <ComingSoon label={TILES.find((t) => t.key === screen)?.label} onBack={() => setScreen('home')} />}
        </div>
      </CxRoot>
    </AppLayout>
  );
}

function Home({ onOpen, user, onOpenEtudes }) {
  const isAdmin = ['super_admin', 'admin'].includes(user?.role) || (user?.email || '').toLowerCase() === 'c.perez@wasabicorner.fr';
  const tileLabel = (t) => (t.key === 'etudes' ? (isAdmin ? 'Toutes les études' : 'Mes études CAPEX') : t.label);
  const openTile = (key) => { if (key === 'etudes') { onOpenEtudes(); } else { onOpen(key); } };
  const regular = TILES.filter((t) => !t.wide);
  const wide = TILES.filter((t) => t.wide);
  return (
    <div data-testid="cs-home" className="max-w-xl mx-auto">
      <div className="flex justify-center mb-6"><CxLogoBox /></div>
      <div className="grid grid-cols-2 gap-3">
        {regular.map((t) => (
          <CxTile key={t.key} label={tileLabel(t)} icon={t.Icon} iconColor={t.color} badge={t.badge} onClick={() => openTile(t.key)} testid={`cs-tile-${t.key}`} />
        ))}
      </div>
      {wide.map((t) => (
        <div className="mt-3" key={t.key}>
          <CxTile label={tileLabel(t)} icon={t.Icon} iconColor={t.color} wide onClick={() => openTile(t.key)} testid={`cs-tile-${t.key}`} />
        </div>
      ))}
    </div>
  );
}

function ComingSoon({ label, onBack }) {
  return (
    <div data-testid="cs-coming-soon">
      <CxBack label="Retour à l'accueil" onClick={onBack} testid="cs-back" />
      <div className="py-24 text-center">
        <h2 className="cx-h1">{label}</h2>
        <p className="mt-2 cx-muted">Module en cours de développement. Nous le construisons dans l'ordre convenu après votre validation.</p>
      </div>
    </div>
  );
}

function CompteAccounts({ api, user, kind, onBack }) {
  const isClient = kind === 'Client';
  const cfg = isClient
    ? { title: 'Compte Clients', sub: 'corners en exploitation', ph: 'Rechercher un client (nom, enseigne, ville, CP)…', hk: 'cs_client_history', tone: 'client', badge: 'Client' }
    : { title: 'Compte Prospects', sub: 'enseignes ciblées', ph: 'Rechercher un prospect (nom, enseigne, ville, CP)…', hk: 'cs_prospect_history', tone: 'warn', badge: 'Prospect' };
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState(() => { try { return JSON.parse(localStorage.getItem(cfg.hk) || '[]'); } catch { return []; } });
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async (query) => {
    setLoading(true);
    try {
      const r = await api().get('/capex-sales/accounts', { params: { q: query, type: kind, limit: 30 } });
      setItems(r.data.items || []);
    } catch (e) { setItems([]); } finally { setLoading(false); }
  }, [api, kind]);

  useEffect(() => { const t = setTimeout(() => { if (q.trim().length >= 1) load(q); else setItems([]); }, 250); return () => clearTimeout(t); }, [q, load]);

  const openAcc = (a) => {
    const entry = { id: a.id, name: a.name, enseigne: a.enseigne, city: a.city, cp: a.cp };
    const next = [entry, ...history.filter((h) => h.id !== a.id)].slice(0, 5);
    setHistory(next); localStorage.setItem(cfg.hk, JSON.stringify(next));
    setOpenId(a.id);
  };

  if (openId) return <AccountFiche api={api} user={user} kind={kind} id={openId} onBack={() => setOpenId(null)} onConverted={() => { setOpenId(null); }} />;

  const showList = q.trim().length >= 1;
  const row = (a, onClick, testid) => (
    <CxItem key={a.id} avatar={initials(a.name)} title={a.name}
      subtitle={[a.enseigne, [a.city, a.cp].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}
      right={<CxBadge tone={cfg.tone}>{cfg.badge}</CxBadge>} onClick={onClick} testid={testid} />
  );

  return (
    <div data-testid={`cs-${kind.toLowerCase()}s`}>
      <CxBack label="Retour à l'accueil" onClick={onBack} testid="cs-back" />
      <div className="flex items-center justify-between mb-4">
        <h1 className="cx-h1">{cfg.title}</h1>
        <span className="text-xs cx-muted-3">{cfg.sub}</span>
      </div>
      <div className="mb-5">
        <CxSearch value={q} onChange={setQ} placeholder={cfg.ph} testid={`cs-${kind.toLowerCase()}s-search`} autoFocus />
      </div>

      {!showList && history.length > 0 && (
        <>
          <p className="cx-micro mb-2">Historique de recherche</p>
          <div className="space-y-2">{history.map((h) => row(h, () => setOpenId(h.id), `cs-acc-row-${h.id}`))}</div>
        </>
      )}
      {!showList && history.length === 0 && <p className="cx-empty">Recherchez {isClient ? 'un client' : 'un prospect'} pour ouvrir sa fiche.</p>}

      {showList && (loading ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>
        : items.length === 0 ? <p className="cx-empty">Aucun compte ne correspond à votre recherche.</p>
          : <div className="space-y-2">{items.map((a) => row(a, () => openAcc(a), `cs-acc-row-${a.id}`))}</div>)}
    </div>
  );
}

const TABS = ['Détails', 'Contacts', 'Opportunités', 'Comptes rendus', 'Écritures comptables', 'Fichiers'];

function AccountFiche({ api, user, kind, id, onBack, onConverted }) {
  const isClient = kind === 'Client';
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('Détails');
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openOpp, setOpenOpp] = useState(null);
  const [openCr, setOpenCr] = useState(null);
  const [crForm, setCrForm] = useState(false);
  const [oppForm, setOppForm] = useState(false);
  const [actForm, setActForm] = useState(false);
  const canEdit = canConfigCs(user);
  const refresh = useCallback(() => { api().get(`/capex-sales/accounts/${id}/full`).then((r) => setData(r.data)).catch(() => {}); }, [api, id]);
  useEffect(() => { refresh(); api().get('/capex-sales/section-order').then((r) => setOrder(r.data.order)).catch(() => setOrder(null)); }, [api, id, refresh]);
  if (!data) return <div className="py-24 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>;
  const acc = data.account;
  const crList = data.activities.filter((a) => ['Compte-rendu', 'RDV', 'Appel'].includes(a.type));
  const recentCrs = [...crList].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 5);
  const activeOpps = (data.opportunities || []).filter((o) => !['gagnee', 'perdu'].includes(o.stage) && !['perdu', 'won', 'gagnee'].includes(o.status));
  const openActions = (data.activities || []).filter((a) => a.type === 'Tâche' && !a.done);

  if (openOpp) return <OppFiche api={api} id={openOpp.id} opp={openOpp} stages={data.stages || []} onBack={() => setOpenOpp(null)} onChanged={refresh} />;
  if (openCr) return <CrDetail cr={openCr} onBack={() => setOpenCr(null)} />;
  if (crForm) return <CRForm api={api} user={user} presetStore={{ ...acc, type: acc.type || kind }} onBack={() => setCrForm(false)} onSaved={() => { setCrForm(false); refresh(); }} />;
  if (oppForm) return <OppCreate api={api} user={user} stages={data.stages || []} presetAcc={{ ...acc, type: acc.type || kind }} onBack={() => setOppForm(false)} onCreated={() => { setOppForm(false); refresh(); }} />;
  if (actForm) return <ActionsMener api={api} user={user} presetStore={{ ...acc, type: acc.type || kind }} onBack={() => { setActForm(false); refresh(); }} />;

  const convert = async () => {
    if (!window.confirm(`Convertir « ${acc.name} » en client ?`)) return;
    setBusy(true);
    try { await api().patch(`/capex-sales/accounts/${id}`, { type: 'Client' }); toast.success('Compte converti en client'); onConverted && onConverted(); }
    catch (e) { toast.error('Erreur lors de la conversion'); } finally { setBusy(false); }
  };

  return (
    <div data-testid="cs-account-fiche">
      <CxBack label="Retour à la recherche" onClick={onBack} testid="cs-fiche-back" />
      <div className="flex items-start gap-3 mb-4">
        <span className="cx-tile__icon" style={{ width: 48, height: 48, borderRadius: 14, flexShrink: 0 }}><Handshake className="w-6 h-6" style={{ color: 'var(--accent)' }} /></span>
        <div className="min-w-0">
          <h1 className="cx-h1">{acc.name}</h1>
          <div className="text-xs cx-muted">Type : {kind} · {[acc.enseigne, [acc.city, acc.cp].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}</div>
        </div>
      </div>
      <div className="flex gap-2 mb-4 flex-wrap">
        <button className="cx-actionbtn primary" style={{ flex: 1, marginTop: 0, minWidth: 120 }} onClick={() => setCrForm(true)} data-testid="cs-fiche-new-cr"><Plus className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Compte rendu</button>
        <button className="cx-actionbtn ghost" style={{ flex: 1, marginTop: 0, minWidth: 120 }} onClick={() => setOppForm(true)} data-testid="cs-fiche-new-opp"><Plus className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Opportunité</button>
        <button className="cx-actionbtn ghost" style={{ flex: 1, marginTop: 0, minWidth: 120 }} onClick={() => setActForm(true)} data-testid="cs-fiche-new-action"><Plus className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Action</button>
      </div>
      <CxTabs tabs={TABS} active={tab} onChange={setTab} prefix="cs-fiche-tab" />

      {tab === 'Détails' && <Details acc={acc} fields={data.fields} kind={kind} opportunities={data.opportunities} order={order} api={api} accountId={id} canEdit={canEdit} onSaved={refresh} />}
      {tab === 'Détails' && (
        <div className="mt-6 space-y-5" data-testid="cs-fiche-history">
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="cx-micro" style={{ margin: 0 }}>Actions à mener ({openActions.length})</p>
            </div>
            {openActions.length === 0 ? <CxEmpty>Aucune action en cours.</CxEmpty> : (
              <div className="space-y-2">{openActions.slice(0, 5).map((a) => {
                const days = overdueDays(a.due_date);
                return (
                <div key={a.id} className="flex items-center gap-3 p-3 cx-surface" data-testid={`cs-fiche-hist-action-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate" style={{ color: 'var(--accent)' }}>{a.subject}</div>
                    <div className="flex items-center gap-2 flex-wrap mt-1"><CrOwnerChip name={a.owner_name} /><span className="text-xs" style={{ color: 'var(--ink-3)' }}>Créé {fmtFrDate(a.created_at)}</span><span className="text-xs font-semibold" style={{ color: days ? '#eb8c74' : 'var(--ink-2)' }}>Échéance {a.due_date ? fmtFrDate(a.due_date) : '—'}</span></div>
                  </div>
                  {days > 0 && <CxBadge tone="alert">{days} j</CxBadge>}
                </div>
                );
              })}</div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="cx-micro" style={{ margin: 0 }}>Opportunités en cours ({activeOpps.length})</p>
              {data.opportunities.length > 0 && <button className="cx-micro" style={{ color: 'var(--accent)', fontWeight: 700 }} onClick={() => setTab('Opportunités')} data-testid="cs-fiche-history-opps-all">Voir tout ›</button>}
            </div>
            {activeOpps.length === 0 ? <CxEmpty>Aucune opportunité active.</CxEmpty> : (
              <div className="space-y-2">{activeOpps.slice(0, 5).map((o) => (
                <div key={o.id} className="flex items-center gap-3 p-3 cx-surface" onClick={() => setOpenOpp(o)} style={{ cursor: 'pointer' }} data-testid={`cs-fiche-hist-opp-${o.id}`}>
                  <div className="flex-1 min-w-0"><div className="font-semibold truncate" style={{ color: 'var(--ink)' }}>{o.name}</div><div className="text-xs truncate cx-muted">{oppStageLabel(o.stage, data.stages)} · {o.probability}%</div></div>
                  {o.ca_annuel_previsionnel ? <span className="cx-num font-bold flex-shrink-0" style={{ color: 'var(--accent)' }}>{fmtK(o.ca_annuel_previsionnel)}</span> : null}
                  <span className="cx-muted flex-shrink-0">›</span>
                </div>
              ))}</div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="cx-micro" style={{ margin: 0 }}>Comptes rendus récents ({crList.length})</p>
              {crList.length > 0 && <button className="cx-micro" style={{ color: 'var(--accent)', fontWeight: 700 }} onClick={() => setTab('Comptes rendus')} data-testid="cs-fiche-history-crs-all">Voir tout ›</button>}
            </div>
            {recentCrs.length === 0 ? <CxEmpty>Aucun compte rendu.</CxEmpty> : (
              <div className="space-y-2">{recentCrs.map((a) => (
                <div key={a.id} className="flex items-center gap-3 p-3 cx-surface" onClick={() => setOpenCr(a)} style={{ cursor: 'pointer' }} data-testid={`cs-fiche-hist-cr-${a.id}`}>
                  <div className="flex-1 min-w-0"><div className="font-semibold truncate mb-1" style={{ color: 'var(--ink)' }}>{a.subject}</div><div className="flex items-center gap-2 flex-wrap"><CrOwnerChip name={a.owner_name} /><span className="text-xs" style={{ color: 'var(--ink-3)', fontWeight: 600 }}>{fmtFrDate(a.created_at)}</span></div></div>
                  <span className="cx-muted flex-shrink-0">›</span>
                </div>
              ))}</div>
            )}
          </div>
        </div>
      )}
      {tab === 'Contacts' && (
        <div>
          <div className="flex items-center justify-between mb-3"><h3 className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>Contacts ({data.contacts.length})</h3></div>
          {data.contacts.length === 0 ? <CxEmpty>Aucun contact.</CxEmpty>
            : <div className="space-y-2">{data.contacts.map((c) => <ContactCard key={c.id} c={c} showAccount={false} testid={`cs-acc-contact-${c.id}`} />)}</div>}
        </div>
      )}
      {tab === 'Opportunités' && <ListBlock title={`Opportunités (${data.opportunities.length})`} empty="Aucune opportunité."
        rows={data.opportunities.map((o) => ({ id: o.id, t: o.name, s: `${oppStageLabel(o.stage, data.stages)} · ${o.probability}%`, r: fmtK(o.ca_annuel_previsionnel) }))}
        onRow={(oid) => setOpenOpp(data.opportunities.find((o) => o.id === oid))} />}
      {tab === 'Comptes rendus' && (crList.length === 0 ? <CxEmpty>Aucun compte rendu.</CxEmpty> : (
        <div>
          <p className="cx-micro mb-2">Comptes rendus ({crList.length})</p>
          <div className="space-y-2">{crList.map((a) => (
            <div key={a.id} className="flex items-start gap-3 p-3 cx-surface" onClick={() => setOpenCr(a)} style={{ cursor: 'pointer' }} data-testid={`cs-listrow-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate mb-1" style={{ color: 'var(--ink)' }}>{a.subject}</div>
                <div className="flex items-center gap-2 flex-wrap mb-1"><CrOwnerChip name={a.owner_name} testid={`cs-cr-owner-${a.id}`} /><span className="text-xs" style={{ color: 'var(--ink-3)', fontWeight: 600 }}>{fmtFrDate(a.created_at)}</span></div>
                {(() => { const b = cleanCrBody(a.body, a.custom_fields); const s = b || a.type; return s ? <div className="text-xs truncate cx-muted">{s}</div> : null; })()}
              </div>
              <span className="cx-muted flex-shrink-0">›</span>
            </div>
          ))}</div>
        </div>
      ))}
      {tab === 'Écritures comptables' && <CxEmpty>Aucune écriture comptable disponible.</CxEmpty>}
      {tab === 'Fichiers' && <DocsPanel relatedType="account" relatedId={id} relatedName={acc.name} title="Fichiers" emptyLabel="Aucun fichier joint. Ajoutez un Excel, un PDF ou une photo." />}

      {!isClient && tab === 'Détails' && (
        <div className="mt-5 space-y-2">
          <button className="cx-actionbtn primary" onClick={convert} disabled={busy} data-testid="cs-prospect-convert">{busy ? 'Conversion…' : 'Convertir en client'}</button>
          <button className="cx-actionbtn ghost" onClick={() => toast.success('Relance planifiée (à câbler avec Actions à mener)')} data-testid="cs-prospect-relance">Planifier une relance</button>
        </div>
      )}
    </div>
  );
}

function EditFieldInput({ f, value, onChange }) {
  const st = { background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--ink)', borderRadius: 8, padding: '7px 10px', fontSize: 13, width: '100%' };
  if (f.type === 'textarea') return <textarea style={{ ...st, minHeight: 60, resize: 'vertical' }} value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={`cs-edit-input-${f.key}`} />;
  if (f.type === 'checkbox') return (
    <select style={st} value={value ? '1' : '0'} onChange={(e) => onChange(e.target.value === '1')} data-testid={`cs-edit-input-${f.key}`}>
      <option value="0">Non</option><option value="1">Oui</option>
    </select>
  );
  if (f.type === 'select' || f.type === 'multiselect') return (
    <select style={st} value={value ?? ''} onChange={(e) => onChange(e.target.value)} data-testid={`cs-edit-input-${f.key}`}>
      <option value="">—</option>{(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
  const type = f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text';
  return <input type={type} style={st} value={value ?? ''} onChange={(e) => onChange(f.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)} data-testid={`cs-edit-input-${f.key}`} />;
}

function EditableBlock({ title, testid, defaultOpen, fields, values, isStd, viewContent, canEdit, api, accountId, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const start = () => { const d = {}; fields.forEach((f) => { d[f.key] = values[f.key]; }); setDraft(d); setEditing(true); };
  const save = async () => {
    setSaving(true);
    try {
      const changed = {};
      fields.forEach((f) => { if (draft[f.key] !== values[f.key]) changed[f.key] = draft[f.key] === '' ? null : draft[f.key]; });
      if (Object.keys(changed).length === 0) { setEditing(false); return; }
      const body = isStd ? { standard: changed } : { custom_fields: changed };
      await api().patch(`/capex-sales/accounts/${accountId}/field-edit`, body);
      toast.success('Fiche mise à jour');
      setEditing(false);
      onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };
  return (
    <CxAccordion title={title} defaultOpen={defaultOpen} testid={testid}
      action={canEdit && !editing ? (
        <button onClick={start} data-testid={`${testid}-edit`} title="Modifier cette rubrique"
          className="inline-flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--accent)', cursor: 'pointer' }}>
          <Pencil className="w-3.5 h-3.5" />
        </button>
      ) : null}>
      {!editing ? (
        viewContent || <Grid rows={fields.map((f) => [f.label, fmtVal(f, values[f.key])])} />
      ) : (
        <div className="space-y-3">
          {fields.map((f) => (
            <div key={f.key}>
              <label className="cx-label">{f.label}</label>
              <EditFieldInput f={f} value={draft[f.key]} onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))} />
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <button className="cx-actionbtn primary" style={{ flex: 1 }} onClick={save} disabled={saving} data-testid={`${testid}-save`}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
            <button className="cx-actionbtn ghost" style={{ flex: 1 }} onClick={() => setEditing(false)} disabled={saving} data-testid={`${testid}-cancel`}>Annuler</button>
          </div>
        </div>
      )}
    </CxAccordion>
  );
}

function Details({ acc, fields, kind, opportunities, order, api, accountId, canEdit, onSaved }) {
  const sections = useMemo(() => {
    const bySec = {};
    (fields || []).forEach((f) => { const s = f.section || 'Autres'; (bySec[s] = bySec[s] || []).push(f); });
    return bySec;
  }, [fields]);
  const cf = acc.custom_fields || {};
  const isProspect = kind === 'Prospect';

  const accountFields = [
    { key: 'enseigne', label: "Nom de l'enseigne GMS", type: 'text' },
    { key: 'name', label: 'Nom du compte', type: 'text' },
    { key: 'address', label: 'Adresse (ligne 1)', type: 'text' },
    { key: 'cp', label: 'Code postal', type: 'text' },
    { key: 'city', label: 'Ville', type: 'text' },
    { key: 'phone', label: 'Téléphone', type: 'phone' },
    { key: 'owner_name', label: 'Propriétaire du compte', type: 'text' },
  ];
  const accountView = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
      <div>
        <CxKV label="Nom de l'enseigne GMS" value={acc.enseigne || '—'} />
        <CxKV label="Nom du compte" value={acc.name || '—'} />
        <CxKV label="Adresse (ligne 1)" value={acc.address || '—'} />
        <CxKV label="Code postal" value={acc.cp || '—'} />
        <CxKV label="Ville" value={acc.city || '—'} />
        <CxKV label="Téléphone" value={acc.phone || '—'} />
      </div>
      <div><CxKV label="Propriétaire du compte" value={acc.owner_name || '—'} /></div>
    </div>
  );
  const accountBlock = (
    <EditableBlock key="Account Information" title="Account Information" testid="cs-acc-account" defaultOpen
      fields={accountFields} values={acc} isStd viewContent={accountView}
      canEdit={canEdit} api={api} accountId={accountId} onSaved={onSaved} />
  );

  const caFields = [
    { key: 'ca_previsionnel_annuel', label: 'CA prévisionnel annuel (run rate) — €', type: 'number' },
  ];
  const caBlock = (
    <EditableBlock key="Chiffre d'affaires" title="Chiffre d'affaires" testid="cs-acc-ca" defaultOpen
      fields={caFields} values={cf} isStd={false}
      canEdit={canEdit} api={api} accountId={accountId} onSaved={onSaved} />
  );

  if (isProspect) {
    return (
      <div className="space-y-3">
        {accountBlock}
        <CxAccordion title="Qualification commerciale" defaultOpen testid="cs-acc-qualif">
          {(opportunities && opportunities.length > 0) ? (
            <Grid rows={[
              ['Opportunités ouvertes', String(opportunities.length)],
              ['Meilleure probabilité', Math.max(...opportunities.map((o) => o.probability || 0)) + ' %'],
              ['CA potentiel cumulé', fmtK(opportunities.reduce((s, o) => s + (o.ca_annuel_previsionnel || 0), 0))],
              ['Étape la plus avancée', opportunities[0].stage_label || '—'],
            ]} />
          ) : <p className="cx-muted text-sm">Aucune opportunité qualifiée. La conversion en client activera les blocs corner.</p>}
        </CxAccordion>
      </div>
    );
  }

  const defaultOrder = ['Account Information', 'Informations générales', 'Propriété du Corner', 'RH & Organisation Corner', 'Informations complémentaires', 'Autres'];
  let seq = (order && order.length ? [...order] : defaultOrder);
  Object.keys(sections).forEach((s) => { if (!seq.includes(s)) seq.push(s); });
  return (
    <div className="space-y-3">
      {seq.map((s) => {
        if (s === 'Account Information') return accountBlock;
        if (!sections[s]) return null;
        return (
          <EditableBlock key={s} title={s} testid={`cs-acc-${s}`} defaultOpen
            fields={sections[s]} values={cf} isStd={false}
            canEdit={canEdit} api={api} accountId={accountId} onSaved={onSaved} />
        );
      })}
      {caBlock}
    </div>
  );
}

function Grid({ rows }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
      {rows.map(([l, v], i) => <CxKV key={i} label={l} value={v} />)}
    </div>
  );
}

const CR_OWNER_PALETTE = [
  { color: '#f59e9e', bg: 'rgba(239,68,68,.16)', border: 'rgba(239,68,68,.5)' },
  { color: '#f7b267', bg: 'rgba(234,140,30,.16)', border: 'rgba(234,140,30,.5)' },
  { color: '#f6d365', bg: 'rgba(217,168,32,.16)', border: 'rgba(217,168,32,.5)' },
  { color: '#a3e635', bg: 'rgba(132,204,22,.16)', border: 'rgba(132,204,22,.5)' },
  { color: '#6ee7b7', bg: 'rgba(16,185,129,.16)', border: 'rgba(16,185,129,.5)' },
  { color: '#5eead4', bg: 'rgba(20,184,166,.16)', border: 'rgba(20,184,166,.5)' },
  { color: '#7dd3fc', bg: 'rgba(56,189,248,.16)', border: 'rgba(56,189,248,.5)' },
  { color: '#93c5fd', bg: 'rgba(59,130,246,.16)', border: 'rgba(59,130,246,.5)' },
  { color: '#c4b5fd', bg: 'rgba(139,92,246,.16)', border: 'rgba(139,92,246,.5)' },
  { color: '#d8b4fe', bg: 'rgba(168,85,247,.16)', border: 'rgba(168,85,247,.5)' },
  { color: '#f0abfc', bg: 'rgba(217,70,239,.16)', border: 'rgba(217,70,239,.5)' },
  { color: '#f9a8d4', bg: 'rgba(236,72,153,.16)', border: 'rgba(236,72,153,.5)' },
  { color: '#fda4af', bg: 'rgba(244,63,94,.16)', border: 'rgba(244,63,94,.5)' },
  { color: '#bef264', bg: 'rgba(163,192,20,.16)', border: 'rgba(163,192,20,.5)' },
  { color: '#67e8f9', bg: 'rgba(6,182,212,.16)', border: 'rgba(6,182,212,.5)' },
  { color: '#fcd34d', bg: 'rgba(245,158,11,.16)', border: 'rgba(245,158,11,.5)' },
];
function crOwnerColor(name) {
  const s = (name || '—').trim() || '—';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 131 + s.charCodeAt(i)) >>> 0;
  return CR_OWNER_PALETTE[h % CR_OWNER_PALETTE.length];
}
function fmtFrDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (!isNaN(d.getTime())) { const dd = String(d.getDate()).padStart(2, '0'); const mm = String(d.getMonth() + 1).padStart(2, '0'); return `${dd}/${mm}/${d.getFullYear()}`; }
  const p = (iso || '').slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : (iso || '');
}
function CrOwnerChip({ name, testid }) {
  const c = crOwnerColor(name);
  return <span data-testid={testid} className="inline-flex items-center" style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 999, padding: '1px 9px', fontSize: 11, fontWeight: 700, maxWidth: 170, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || '—'}</span>;
}

function cleanCrBody(body, cf) {
  let b = body || '';
  cf = cf || {};
  if (/^\s*type de visite\s*:/i.test(b)) {
    const rIdx = b.toLowerCase().indexOf('résultat');
    if (rIdx !== -1 && cf.resultat) {
      const after = b.indexOf(cf.resultat, rIdx);
      if (after !== -1) b = b.slice(after + cf.resultat.length);
    } else if (rIdx !== -1) {
      const nl = b.indexOf('\n', rIdx);
      if (nl !== -1) b = b.slice(nl + 1);
    }
  }
  return b.replace(/^[\s·\-–—:|]+/, '').trim();
}


function ListBlock({ title, rows, empty, onRow }) {
  if (!rows || rows.length === 0) return <CxEmpty>{empty}</CxEmpty>;
  return (
    <div>
      <p className="cx-micro mb-2">{title}</p>
      <div className="space-y-2">{rows.map((r) => (
        <div key={r.id} className="flex items-center gap-3 p-3 cx-surface" onClick={onRow ? () => onRow(r.id) : undefined} style={onRow ? { cursor: 'pointer' } : undefined} data-testid={onRow ? `cs-listrow-${r.id}` : undefined}>
          <div className="flex-1 min-w-0"><div className="font-semibold truncate" style={{ color: 'var(--ink)' }}>{r.t}</div><div className="text-xs truncate cx-muted">{r.s}</div></div>
          {r.r && <span className="cx-num font-bold flex-shrink-0" style={{ color: 'var(--accent)' }}>{r.r}</span>}
          {onRow && <span className="cx-muted flex-shrink-0">›</span>}
        </div>
      ))}</div>
    </div>
  );
}

const DOC_ACCEPT = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,image/*,application/pdf';
const docIcon = (d) => ((d.content_type || '').startsWith('image/') ? ImageIcon : FileText);
const fmtSize = (b) => { if (!b) return ''; if (b < 1024) return b + ' o'; if (b < 1048576) return (b / 1024).toFixed(0) + ' Ko'; return (b / 1048576).toFixed(1) + ' Mo'; };

function DocsPanel({ relatedType, relatedId, relatedName, title = 'Fichiers', emptyLabel = 'Aucun fichier joint.' }) {
  const { api, token, user } = useAuth();
  const [docs, setDocs] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const load = useCallback(() => {
    api().get('/capex-sales/documents', { params: { related_type: relatedType, related_id: relatedId } })
      .then((r) => setDocs(r.data.items || [])).catch(() => setDocs([]));
  }, [api, relatedType, relatedId]);
  useEffect(() => { load(); }, [load]);
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append('file', f);
        await api().post('/capex-sales/documents/upload', fd, { params: { related_type: relatedType, related_id: relatedId, related_name: relatedName || '' } });
      }
      toast.success(files.length > 1 ? `${files.length} fichiers ajoutés` : 'Fichier ajouté');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload impossible'); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  const del = async (d) => {
    if (!window.confirm(`Supprimer « ${d.original_filename} » ?`)) return;
    try { await api().delete(`/capex-sales/documents/${d.id}`); toast.success('Fichier supprimé'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Suppression impossible'); }
  };
  const dlUrl = (d) => `${BACKEND_URL}/api/capex-sales/documents/${d.id}/download?auth=${token}`;
  return (
    <div data-testid={`cs-docs-${relatedType}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="cx-micro" style={{ margin: 0 }}>{title} ({docs ? docs.length : '…'})</p>
        <button className="cx-actionbtn primary" style={{ marginTop: 0, width: 'auto', padding: '8px 14px' }} onClick={() => inputRef.current && inputRef.current.click()} disabled={busy} data-testid={`cs-docs-upload-${relatedType}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline-block mr-1" style={{ verticalAlign: '-3px' }} /> : <Upload className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />}
          Ajouter
        </button>
        <input ref={inputRef} type="file" accept={DOC_ACCEPT} multiple onChange={onPick} style={{ display: 'none' }} data-testid={`cs-docs-input-${relatedType}`} />
      </div>
      <p className="text-xs cx-muted-3 mb-2">Formats acceptés : Excel, PDF, photos (JPG/PNG), Word, PowerPoint — max 25 Mo.</p>
      {docs === null ? <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>
        : docs.length === 0 ? <CxEmpty>{emptyLabel}</CxEmpty>
          : <div className="space-y-2">{docs.map((d) => { const I = docIcon(d); const isImg = (d.content_type || '').startsWith('image/'); return (
            <div key={d.id} className="flex items-center gap-3 p-3 cx-surface" data-testid={`cs-doc-${d.id}`}>
              {isImg
                ? <a href={dlUrl(d)} target="_blank" rel="noopener noreferrer" className="flex-shrink-0" data-testid={`cs-doc-thumb-${d.id}`}><img src={dlUrl(d)} alt={d.original_filename} loading="lazy" style={{ width: 44, height: 44, borderRadius: 8, objectFit: 'cover', display: 'block' }} /></a>
                : <I className="w-5 h-5 flex-shrink-0" style={{ color: '#eb8c4a' }} />}
              <a href={dlUrl(d)} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0" data-testid={`cs-doc-open-${d.id}`}>
                <div className="font-semibold truncate" style={{ color: 'var(--ink)' }}>{d.original_filename}</div>
                <div className="text-xs cx-muted truncate">{fmtSize(d.size)}{d.uploaded_by_name ? ` · ${d.uploaded_by_name}` : ''} · {fmtFrDate(d.created_at)}</div>
              </a>
              {(user?.role === 'admin' || user?.role === 'super_admin' || d.uploaded_by === user?.id) && (
                <button className="cx-stepbtn" style={{ width: 30, height: 30, color: '#eb563b' }} onClick={() => del(d)} title="Supprimer" data-testid={`cs-doc-delete-${d.id}`}><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ); })}</div>}
    </div>
  );
}


function CrDetail({ cr, onBack }) {
  const cf = cr.custom_fields || {};
  return (
    <div data-testid="cs-cr-detail">
      <CxBack label="Retour aux comptes rendus" onClick={onBack} testid="cs-cr-detail-back" />
      <h1 className="cx-h1">{cr.subject || 'Compte rendu'}</h1>
      <div className="flex items-center gap-2 flex-wrap mb-3"><CrOwnerChip name={cr.owner_name} testid="cs-cr-detail-owner" /><span className="text-xs" style={{ color: 'var(--ink-3)', fontWeight: 600 }}>{fmtFrDate(cr.created_at)}</span></div>
      <CxAccordion title="Détail du compte rendu" defaultOpen testid="cs-cr-detail-acc">
        <Grid rows={[
          ['Compte', cr.related_name || '—'], ['Objet', cf.objet || cr.subject || '—'],
          ['Origine / Type de visite', cf.origine || '—'], ['Résultat', cf.resultat || '—'],
          ['Commercial', cr.owner_name || '—'], ['Date', fmtFrDate(cr.created_at) || '—'],
        ]} />
        {(() => {
          const desc = cleanCrBody(cr.body, cf);
          if (!desc) return null;
          const points = desc.split(/\s*·\s*|\n+/).map((s) => s.trim()).filter(Boolean);
          return (
            <div className="mt-2 cx-kv">
              <span className="cx-kv__label">Description</span>
              <div className="cx-kv__value" style={{ display: 'flex', flexDirection: 'column', gap: 4 }} data-testid="cs-cr-desc-list">
                {points.map((p, i) => {
                  const idx = p.indexOf(' : ');
                  return (
                    <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'baseline' }} data-testid={`cs-cr-desc-line-${i}`}>
                      <span style={{ color: 'var(--accent)', flexShrink: 0 }}>•</span>
                      <span>{idx > 0 ? (<><strong>{p.slice(0, idx)}</strong>{p.slice(idx)}</>) : p}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </CxAccordion>
      <div className="mt-5">
        <DocsPanel relatedType="activity" relatedId={cr.id} relatedName={cr.related_name || cr.subject} title="Pièces jointes" emptyLabel="Aucune pièce jointe. Ajoutez une photo ou un fichier à ce compte rendu." />
      </div>
    </div>
  );
}

const TRANCHES = [
  { key: 'hot', nm: 'HOT', rg: '90-100 %', Icon: Flame, color: '#eb6a56', min: 90, max: 100 },
  { key: 'encours', nm: 'En cours', rg: '50-75 %', Icon: Clock, color: '#5b9bd5', min: 50, max: 74 },
  { key: 'caliente', nm: 'Caliente', rg: '75-90 %', Icon: Flame, color: '#ef7623', min: 75, max: 89 },
  { key: 'merite', nm: 'Ça le mérite', rg: '0-50 %', Icon: Trophy, color: '#7dc242', min: 0, max: 49 },
];
function pctColor(p) { if (p >= 90) return '#eb6a56'; if (p >= 75) return '#ef7623'; if (p >= 50) return '#5b9bd5'; return '#7dc242'; }

const OPP_STAGES5 = [
  { key: 'identification', label: 'Identification des besoins', prob: 20, band: [0, 40] },
  { key: 'proposition', label: 'Proposition de solution', prob: 55, band: [41, 70] },
  { key: 'contractualisation', label: 'Contractualisation', prob: 85, band: [71, 99] },
  { key: 'gagnee', label: 'Fermée gagnée', prob: 100, band: [100, 100], win: true },
  { key: 'perdu', label: 'Fermée perdu', prob: 0, band: [0, 0], lose: true },
];
const oppStageLabel = (k, backendStages) => (OPP_STAGES5.find((s) => s.key === k) || (backendStages || []).find((s) => s.key === k) || {}).label || k || '—';
const OPP_ORIGINES = ['Appel téléprospectrice', 'Mail Téléprospectrice', 'Appel', 'Prospection Physique', 'Sur RDV Commercial', 'Sur RDV Commercial DR/BM', 'Sur RDV Télépro', 'Sur RDV Visio', 'Sans RDV Suivi Client', 'Passage du DR', 'Passage du BM', 'Passage du SAV', 'Salon professionnel'];
const OPP_SIGN_ORIGINES = ['Client CCD', 'Whitespace', 'Take over Hanagroup', 'Take over Papylee', 'Take over Kellydeli (KD)', 'Take over Sushiman', 'Take over Sushishop', 'Take over Maki chef', 'Take over Katana', 'Take over Yumisushi', 'Take over Côté Sushi', 'Take over Bento Sushi', 'Take over autre'];
const OPP_CONCEPTS = ['Wasabi', 'Mme Wang', 'Sorae'];
const OPP_DUREES = ['12 mois', '24 mois', '36 mois', '48 mois', '60 mois'];
const trimestreFromDate = (v) => { if (!v) return '—'; const d = new Date(v); if (isNaN(d.getTime())) return '—'; return `Trimestre ${Math.floor(d.getMonth() / 3) + 1}`; };

function MultiSelectSearch({ options, value, onChange, max = 2, placeholder = 'Sélectionner…', testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = options.filter((o) => o.toLowerCase().includes(q.toLowerCase()));
  const toggle = (o) => {
    if (value.includes(o)) { onChange(value.filter((x) => x !== o)); return; }
    if (max && value.length >= max) { toast.error(`${max} maximum`); return; }
    onChange([...value, o]);
  };
  return (
    <div style={{ position: 'relative' }} data-testid={testid}>
      <div onClick={() => setOpen((v) => !v)} className="mt-1 px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2" style={{ background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--ink)', cursor: 'pointer', minHeight: 42 }} data-testid={`${testid}-trigger`}>
        <div className="flex flex-wrap gap-1 flex-1 min-w-0">
          {value.length === 0 ? <span className="cx-muted">{placeholder}</span> : value.map((v) => (
            <span key={v} className="cx-chip" style={{ background: 'var(--accent)', color: 'var(--on-accent)', borderColor: 'var(--accent)', fontSize: 12 }} onClick={(e) => { e.stopPropagation(); toggle(v); }}>{v} ×</span>
          ))}
        </div>
        <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--ink-3)' }} />
      </div>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
          <div className="cx-surface" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 4, padding: 0, maxHeight: 300, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,.45)' }}>
            <div style={{ position: 'sticky', top: 0, background: 'var(--tile)', padding: 8, borderBottom: '1px solid var(--line-2)' }}>
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="w-full px-2 py-1.5 rounded-md text-sm" style={{ background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--ink)' }} data-testid={`${testid}-search`} />
            </div>
            <div className="p-1">
              {filtered.length === 0 ? <div className="cx-muted text-sm p-2">Aucun résultat</div> : filtered.map((o) => {
                const on = value.includes(o);
                return (
                  <div key={o} onClick={() => toggle(o)} className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm" style={{ cursor: 'pointer', background: on ? 'rgba(239,118,35,.18)' : 'transparent', color: 'var(--ink)' }} data-testid={`${testid}-opt-${o}`}>
                    <span style={{ width: 16, color: 'var(--accent)' }}>{on ? '✓' : ''}</span>{o}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function OppStageBar({ current, onPick }) {
  const [pending, setPending] = useState(null);
  const doConfirm = (stage, declare) => { setPending(null); onPick(stage, { declare }); };
  return (
    <div style={{ border: '1px solid #bfe3a6', background: 'rgba(191,227,166,.08)', borderRadius: 12, padding: '10px 12px', margin: '4px 0 10px' }} data-testid="cs-opp-stagebar">
      <div className="cx-micro" style={{ color: '#bfe3a6', marginBottom: 6 }}>Étape de l'opportunité — progression</div>
      <div className="flex flex-wrap gap-2">
        {OPP_STAGES5.map((s) => {
          const on = s.key === current;
          const bg = on ? (s.win ? '#7dc242' : s.lose ? '#C0462E' : 'var(--accent)') : 'var(--tile)';
          const col = on ? (s.win ? '#12210b' : '#fff') : 'var(--ink)';
          return <span key={s.key} onClick={() => { if (!on) setPending(s); }} className="cx-chip" style={{ background: bg, color: col, borderColor: on ? bg : 'var(--line-2)', cursor: on ? 'default' : 'pointer' }} data-testid={`cs-opp-stagechip-${s.key}`}>{s.label}</span>;
        })}
      </div>
      <p className="cx-micro" style={{ marginTop: 6 }}>Chaque changement d'étape demande une validation.</p>
      {pending && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} data-testid="cs-opp-stage-modal">
          <div className="cx-surface p-4" style={{ maxWidth: 380, width: '100%' }}>
            <div className="font-semibold mb-1" style={{ color: 'var(--ink)' }}>
              {pending.win ? 'Passage en Fermée gagnée' : pending.lose ? 'Passage en Fermée perdu' : "Changement d'étape"}
            </div>
            <p className="cx-micro mb-3">
              {pending.win ? "Une opportunité fermée gagnée est considérée comme un contrat signé." : pending.lose ? "L'opportunité sera considérée comme un contrat perdu. Merci d'indiquer la raison ensuite." : `Confirmer le passage à « ${pending.label} » ?`}
            </p>
            <div className="space-y-2">
              {pending.win ? (
                <>
                  <button className="cx-actionbtn primary" onClick={() => doConfirm('gagnee', true)} data-testid="cs-opp-stage-confirm-declare">Confirmer et déclarer le contrat</button>
                  <button className="cx-actionbtn ghost" onClick={() => doConfirm('gagnee', false)} data-testid="cs-opp-stage-confirm">Confirmer sans déclarer</button>
                </>
              ) : (
                <button className={`cx-actionbtn ${pending.lose ? 'danger' : 'primary'}`} onClick={() => doConfirm(pending.key, false)} data-testid="cs-opp-stage-confirm">Confirmer</button>
              )}
              <button className="cx-actionbtn ghost" onClick={() => setPending(null)} data-testid="cs-opp-stage-cancel">Annuler</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Opportunites({ api, user, onBack }) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [bandFilter, setBandFilter] = useState(null);
  const [stageFilter, setStageFilter] = useState('');
  const [agedMonths, setAgedMonths] = useState(0);
  const [staleMonths, setStaleMonths] = useState(0);
  const [ownerFilter, setOwnerFilter] = useState('');
  const isAdmin = ['super_admin', 'admin'].includes(user?.role) || (user?.email || '').toLowerCase() === 'c.perez@wasabicorner.fr';
  const load = useCallback(() => { api().get('/capex-sales/opportunities').then((r) => setData(r.data)).catch(() => setData({ items: [], stages: [] })); }, [api]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="py-24 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>;

  if (creating) return <OppCreate api={api} user={user} stages={data.stages} onBack={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />;
  if (openId) return <OppFiche api={api} id={openId} opp={data.items.find((o) => o.id === openId)} stages={data.stages} onBack={() => setOpenId(null)} onChanged={load} />;

  const open = data.items.filter((o) => o.status === 'open');
  const pondere = open.reduce((s, o) => s + (o.forecast || 0), 0);
  const total = open.reduce((s, o) => s + (o.ca_annuel_previsionnel || 0), 0);
  const monthsSince = (iso) => { if (!iso) return null; const d = new Date(iso); if (isNaN(d.getTime())) return null; return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 30.44); };
  const filtered = open.filter((o) => {
    if (bandFilter) { const t = TRANCHES.find((x) => x.key === bandFilter); if (t && !((o.probability || 0) >= t.min && (o.probability || 0) <= t.max)) return false; }
    if (stageFilter && o.stage !== stageFilter) return false;
    if (ownerFilter && o.owner_name !== ownerFilter) return false;
    if (agedMonths) { const m = monthsSince(o.created_at); if (m === null || m < agedMonths) return false; }
    if (staleMonths) { const m = monthsSince(o.updated_at); if (m === null || m < staleMonths) return false; }
    return true;
  });
  const oppOwners = Array.from(new Set(open.map((o) => o.owner_name).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const anyFilter = bandFilter || stageFilter || ownerFilter || agedMonths || staleMonths;
  const resetFilters = () => { setBandFilter(null); setStageFilter(''); setOwnerFilter(''); setAgedMonths(0); setStaleMonths(0); };
  const selSt = { background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--ink)', borderRadius: 10, padding: '6px 8px', fontSize: 13 };
  const bump = async (o, delta, e) => {
    e.stopPropagation();
    const p = Math.max(0, Math.min(100, (o.probability || 0) + delta));
    setData((d) => ({ ...d, items: d.items.map((x) => x.id === o.id ? { ...x, probability: p, forecast: Math.round((x.ca_annuel_previsionnel || 0) * p / 100) } : x) }));
    try { await api().patch(`/capex-sales/opportunities/${o.id}`, { probability: p }); } catch (err) { toast.error('Erreur'); load(); }
  };

  return (
    <div data-testid="cs-opportunites">
      <CxBack label="Retour à l'accueil" onClick={onBack} testid="cs-back" />
      <div className="flex items-center justify-between mb-4">
        <h1 className="cx-h1">Opportunités</h1>
        <span className="text-xs cx-muted-3">Pipeline ouvert</span>
      </div>
      <div className="cx-kpis">
        <div className="cx-kpi" data-testid="cs-kpi-pondere"><div className="v">{fmtK(pondere)}</div><div className="l">Pipeline pondéré (CA × prob.)</div></div>
        <div className="cx-kpi" data-testid="cs-kpi-total"><div className="v">{fmtK(total)}</div><div className="l">CA total ouvert</div></div>
      </div>
      <div className="cx-tranche-grid">
        {TRANCHES.map((t) => {
          const inb = open.filter((o) => (o.probability || 0) >= t.min && (o.probability || 0) <= t.max);
          const val = inb.reduce((s, o) => s + (o.ca_annuel_previsionnel || 0), 0);
          const active = bandFilter === t.key;
          return (
            <div key={t.key} className="cx-tranche" onClick={() => setBandFilter(active ? null : t.key)} style={{ cursor: 'pointer', outline: active ? `2px solid ${t.color}` : 'none', outlineOffset: 2 }} data-testid={`cs-tranche-${t.key}`}>
              <div className="top"><t.Icon className="w-5 h-5" style={{ color: t.color }} /><span className="cx-count" style={{ background: t.color }}>{inb.length}</span></div>
              <div className="nm">{t.nm}</div><div className="rg">{t.rg}</div>
              <div className="val" style={{ color: t.color }}>{fmtK(val)}</div>
            </div>
          );
        })}
      </div>
      <button className="cx-actionbtn primary" onClick={() => setCreating(true)} data-testid="cs-opp-create-btn"><Plus className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Créer une nouvelle opportunité</button>

      <div className="cx-surface p-3 mt-4" data-testid="cs-opp-filters">
        <div className="flex items-center justify-between mb-2"><span className="cx-micro" style={{ fontWeight: 700, letterSpacing: '.05em' }}>FILTRES</span>{anyFilter && <button className="cx-micro" style={{ color: 'var(--accent)', fontWeight: 700 }} onClick={resetFilters} data-testid="cs-opp-filters-reset">Réinitialiser</button>}</div>
        <div className="grid grid-cols-1 gap-2">
          <div><label className="cx-micro">Étape</label>
            <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} style={{ ...selSt, width: '100%', marginTop: 2 }} data-testid="cs-opp-filter-stage">
              <option value="">Toutes les étapes</option>{OPP_STAGES5.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select></div>
          {isAdmin && (
            <div><label className="cx-micro">Commercial (propriétaire)</label>
              <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} style={{ ...selSt, width: '100%', marginTop: 2 }} data-testid="cs-opp-filter-owner">
                <option value="">Tous les commerciaux</option>{oppOwners.map((o) => <option key={o} value={o}>{o}</option>)}
              </select></div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div><label className="cx-micro">Créées depuis plus de</label>
              <select value={agedMonths} onChange={(e) => setAgedMonths(Number(e.target.value))} style={{ ...selSt, width: '100%', marginTop: 2 }} data-testid="cs-opp-filter-aged">
                {[[0, 'Peu importe'], [3, '3 mois'], [6, '6 mois'], [12, '12 mois'], [18, '18 mois'], [24, '24 mois']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
            <div><label className="cx-micro">Sans mouvement depuis</label>
              <select value={staleMonths} onChange={(e) => setStaleMonths(Number(e.target.value))} style={{ ...selSt, width: '100%', marginTop: 2 }} data-testid="cs-opp-filter-stale">
                {[[0, 'Peu importe'], [1, '1 mois'], [3, '3 mois'], [6, '6 mois'], [12, '12 mois']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></div>
          </div>
        </div>
      </div>

      <p className="cx-micro mt-4 mb-2">Opportunités {anyFilter ? 'filtrées' : 'ouvertes'} ({filtered.length}{anyFilter ? ` / ${open.length}` : ''})</p>
      {filtered.length === 0 ? <CxEmpty>{anyFilter ? 'Aucune opportunité ne correspond aux filtres.' : 'Aucune opportunité ouverte. Créez-en une pour démarrer votre pipeline.'}</CxEmpty> : (
        <div className="space-y-2">{filtered.map((o) => (
          <div key={o.id} className="cx-oppitem" onClick={() => setOpenId(o.id)} data-testid={`cs-opp-row-${o.id}`}>
            <span className="cx-pct" style={{ color: pctColor(o.probability), borderColor: pctColor(o.probability) }}>{o.probability}%</span>
            <div className="flex-1 min-w-0"><div className="font-semibold truncate" style={{ color: 'var(--ink)' }}>{o.name}</div><div className="text-xs truncate cx-muted">{oppStageLabel(o.stage, data.stages)} · {fmtK(o.ca_annuel_previsionnel)}/an</div></div>
            <div className="cx-stepper"><button className="cx-stepbtn" onClick={(e) => bump(o, -5, e)} data-testid={`cs-opp-minus-${o.id}`}>−</button><button className="cx-stepbtn" onClick={(e) => bump(o, 5, e)} data-testid={`cs-opp-plus-${o.id}`}>+</button></div>
          </div>
        ))}</div>
      )}
    </div>
  );
}

const OPP_DOSSIER_SECTIONS = [
  { title: 'Dossier technique', keys: [
    ['type_opportunite', "Type d'opportunité"],
    ['client_existant', 'Déjà client Wasabi'],
    ['projet', 'Projet'],
    ['remodeling', 'Remodeling du corner'],
    ['sign_origine', 'Origine signature dossier'],
    ['surface_corner', "Surface d'implémentation (m²)"],
    ['signataires', 'Signataire(s) du contrat'],
    ['demontage', 'Démontage ancien corner'],
  ] },
  { title: 'Investissements & commandes', keys: [
    ['inv_corner', 'Investissement Corner'],
    ['inv_inox', 'Investissement Inox'],
    ['inv_vitrine', 'Investissement Vitrine réfrigérée'],
    ['inv_wok', 'Investissement WOK'],
    ['inv_cellule', 'Investissement Cellule'],
    ['wok_a_commander', 'Wok à commander'],
    ['cellule_a_commander', 'Cellule de refroidissement à commander'],
    ['ecran_a_commander', "Écran d'affichage à commander"],
    ['duree_contrat', 'Durée du contrat (mois)'],
    ['taux_commission', 'Taux de commission'],
    ['reprise_personnel', 'Reprise du personnel'],
  ] },
  { title: 'Dates clés', keys: [
    ['date_dsao', 'Installation DSAO'],
    ['date_mep', 'MEP (ETP)'],
    ['date_ouverture', 'Ouverture prévisionnelle corner'],
    ['date_signature', 'Signature du contrat'],
    ['trimestre_signature', 'Trimestre de signature'],
  ] },
];
function oppCfVal(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  return String(v);
}
function OppDossierSections({ cf }) {
  return (
    <>
      {OPP_DOSSIER_SECTIONS.map((sec) => {
        const rows = sec.keys.filter(([k]) => cf[k] !== undefined && cf[k] !== null && cf[k] !== '' && !(Array.isArray(cf[k]) && cf[k].length === 0));
        if (rows.length === 0) return null;
        return (
          <CxAccordion key={sec.title} title={sec.title} defaultOpen testid={`cs-opp-cf-${sec.title.replace(/[^a-z]/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').toLowerCase()}`}>
            <Grid rows={rows.map(([k, lb]) => [lb, oppCfVal(cf[k])])} />
          </CxAccordion>
        );
      })}
    </>
  );
}

function OppFiche({ api, id, opp, stages, onBack, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  if (!opp) return <div className="py-10"><CxBack label="Retour au pipeline" onClick={onBack} /></div>;
  const cf = opp.custom_fields || {};
  const stageLabel = oppStageLabel(opp.stage, stages);
  const act = async (patch, msg) => { setBusy(true); try { await api().patch(`/capex-sales/opportunities/${id}`, patch); toast.success(msg); onChanged(); onBack(); } catch (e) { toast.error('Erreur'); } finally { setBusy(false); } };
  const onStagePick = (stage, { declare }) => {
    const st = OPP_STAGES5.find((s) => s.key === stage) || {};
    if (stage === 'perdu') {
      const reason = window.prompt('Raison de la perte (obligatoire) :', cf.loss_reason || '');
      if (!reason || !reason.trim()) { toast.error('Raison de la perte requise'); return; }
      act({ stage: 'perdu', probability: 0, status: 'perdu', loss_reason: reason.trim(), custom_fields: { ...cf, loss_reason: reason.trim() } }, 'Opportunité clôturée perdue');
      return;
    }
    const patch = { stage, probability: st.prob ?? opp.probability, status: 'open' };
    act(patch, declare ? 'Opportunité gagnée — pensez à déclarer le contrat (module Contrats)' : `Étape : ${st.label}`);
  };
  const inSt = { background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--ink)', borderRadius: 8, padding: '7px 10px', fontSize: 13, width: '100%' };

  const startEdit = () => {
    setDraft({
      stage: opp.stage || '', nature: opp.nature || '', concept: opp.concept || '',
      ca_annuel_previsionnel: opp.ca_annuel_previsionnel ?? '', probability: opp.probability ?? '',
      date_cible: opp.date_cible || '', origine: cf.origine || '', concurrent: cf.concurrent || '', description: cf.description || '',
    });
    setEditing(true);
  };
  const saveEdit = async () => {
    setSaving(true);
    try {
      const patch = {
        stage: draft.stage || opp.stage,
        nature: draft.nature || null,
        concept: draft.concept || null,
        ca_annuel_previsionnel: draft.ca_annuel_previsionnel === '' ? null : Number(draft.ca_annuel_previsionnel),
        probability: draft.probability === '' ? opp.probability : Math.max(0, Math.min(100, Number(draft.probability))),
        date_cible: draft.date_cible || null,
        custom_fields: { ...cf, origine: draft.origine || null, concurrent: draft.concurrent || null, description: draft.description || null },
      };
      await api().patch(`/capex-sales/opportunities/${id}`, patch);
      toast.success('Opportunité mise à jour');
      setEditing(false);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };

  return (
    <div data-testid="cs-opp-fiche">
      <CxBack label="Retour au pipeline" onClick={onBack} testid="cs-opp-back" />
      <h1 className="cx-h1">{opp.name}</h1>
      <div className="text-xs cx-muted mb-2">Opportunité · {stageLabel}</div>
      <div className="cx-oppmetrics">
        <div className="cx-oppmetric"><div className="v" style={{ color: pctColor(opp.probability) }}>{opp.probability}%</div><div className="l">Probabilité</div></div>
        <div className="cx-oppmetric"><div className="v">{fmtK(opp.ca_annuel_previsionnel)}</div><div className="l">CA annuel</div></div>
        <div className="cx-oppmetric"><div className="v">{fmtK(opp.forecast)}</div><div className="l">Pondéré</div></div>
      </div>
      <OppStageBar current={opp.stage} onPick={onStagePick} />
      <CxAccordion title="Détails de l'opportunité" defaultOpen testid="cs-opp-details"
        action={!editing ? (
          <button onClick={startEdit} data-testid="cs-opp-details-edit" title="Modifier l'opportunité"
            className="inline-flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--accent)', cursor: 'pointer' }}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
        ) : null}>
        {!editing ? (
          <>
            <Grid rows={[
              ['Étape', stageLabel], ["Type d'opportunité", opp.nature || '—'], ['Concept', opp.concept || '—'],
              ['Origine', cf.origine || '—'], ['Concurrent en place', cf.concurrent || '—'], ['Propriétaire', opp.owner_name || '—'],
              ['Date de clôture prévue', opp.date_cible || '—'], ['Compte', opp.account_name || '—'],
            ]} />
            {cf.description && <div className="mt-2"><CxKV label="Description" value={cf.description} /></div>}
          </>
        ) : (
          <div className="space-y-3">
            <div><label className="cx-label">Étape</label>
              <select style={inSt} value={draft.stage} onChange={(e) => setDraft((d) => ({ ...d, stage: e.target.value }))} data-testid="cs-opp-edit-stage">
                {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
            <div><label className="cx-label">Probabilité (%)</label>
              <input type="number" min="0" max="100" style={inSt} value={draft.probability} onChange={(e) => setDraft((d) => ({ ...d, probability: e.target.value }))} data-testid="cs-opp-edit-probability" /></div>
            <div><label className="cx-label">Type d'opportunité</label>
              <input style={inSt} value={draft.nature} onChange={(e) => setDraft((d) => ({ ...d, nature: e.target.value }))} data-testid="cs-opp-edit-nature" /></div>
            <div><label className="cx-label">Concept</label>
              <input style={inSt} value={draft.concept} onChange={(e) => setDraft((d) => ({ ...d, concept: e.target.value }))} data-testid="cs-opp-edit-concept" /></div>
            <div><label className="cx-label">CA annuel prévisionnel (€)</label>
              <input type="number" style={inSt} value={draft.ca_annuel_previsionnel} onChange={(e) => setDraft((d) => ({ ...d, ca_annuel_previsionnel: e.target.value }))} data-testid="cs-opp-edit-ca" /></div>
            <div><label className="cx-label">Date de clôture prévue</label>
              <input style={inSt} placeholder="JJ/MM/AAAA" value={draft.date_cible} onChange={(e) => setDraft((d) => ({ ...d, date_cible: e.target.value }))} data-testid="cs-opp-edit-date" /></div>
            <div><label className="cx-label">Origine</label>
              <input style={inSt} value={draft.origine} onChange={(e) => setDraft((d) => ({ ...d, origine: e.target.value }))} data-testid="cs-opp-edit-origine" /></div>
            <div><label className="cx-label">Concurrent en place</label>
              <input style={inSt} value={draft.concurrent} onChange={(e) => setDraft((d) => ({ ...d, concurrent: e.target.value }))} data-testid="cs-opp-edit-concurrent" /></div>
            <div><label className="cx-label">Description</label>
              <textarea style={{ ...inSt, minHeight: 60, resize: 'vertical' }} value={draft.description} onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))} data-testid="cs-opp-edit-description" /></div>
            <div className="flex gap-2 pt-1">
              <button className="cx-actionbtn primary" style={{ flex: 1 }} onClick={saveEdit} disabled={saving} data-testid="cs-opp-details-save">{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
              <button className="cx-actionbtn ghost" style={{ flex: 1 }} onClick={() => setEditing(false)} disabled={saving} data-testid="cs-opp-details-cancel">Annuler</button>
            </div>
          </div>
        )}
      </CxAccordion>
      {!editing && (
        <div className="mt-4 space-y-3">
          <OppDossierSections cf={cf} />
          {cf.commentaires && <div className="cx-surface p-3"><div className="cx-micro">Commentaires</div><div style={{ color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>{cf.commentaires}</div></div>}
          {cf.loss_reason && opp.status === 'perdu' && <div className="cx-surface p-3"><div className="cx-micro">Raison de la perte</div><div style={{ color: 'var(--ink)' }}>{cf.loss_reason}</div></div>}
        </div>
      )}
    </div>
  );
}

function OppCreate({ api, user, stages, onBack, onCreated, presetAcc }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [acc, setAcc] = useState(presetAcc || null);
  const [panel, setPanel] = useState(1);
  const [signataires, setSignataires] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    stage: 'identification', concept: [], clientExistant: presetAcc ? (presetAcc.type === 'Client' ? 'Oui' : 'Non') : '', origine: '',
    surface: '', signataires: [], signOrigine: '', demontage: '',
    invCorner: '', invInox: '', invVitrine: '', invWok: '', invCellule: '',
    wok: '', ecran: '', duree: '36 mois', commission: '', reprise: '', commentaires: '',
    dateDsao: '', dateMep: '', dateOuv: '', dateSign: '', ca: '', probability: '', dateClot: '', lostReason: '',
  });
  useEffect(() => { const t = setTimeout(() => { if (q.trim().length >= 2) api().get('/capex-sales/accounts', { params: { q, limit: 8 } }).then((r) => setResults(r.data.items || [])); else setResults([]); }, 250); return () => clearTimeout(t); }, [q, api]);
  useEffect(() => { api().get('/capex-sales/signataires').then((r) => setSignataires((r.data.users || []).map((u) => u.name))).catch(() => setSignataires(['Cédric Perez', 'Grégory Pelaprat', 'Thomas Baudrand', 'Edra Piga'])); }, [api]);

  const pickAcc = (a) => { setAcc(a); setResults([]); setQ(''); setF((s) => ({ ...s, clientExistant: a.type === 'Client' ? 'Oui' : 'Non' })); };
  const isC = f.clientExistant === 'Oui';
  const projet = f.clientExistant ? (isC ? 'Corner existant client' : 'Nouveau corner') : '—';
  const nature = f.clientExistant ? (isC ? 'Renouvellement contrat' : 'Nouveau contrat') : '—';
  const remodeling = f.clientExistant ? (isC ? 'Oui' : 'Non') : '—';
  const celluleCmd = f.wok ? (f.wok === 'Oui' ? 'Oui' : 'Non') : '—';
  const trimestre = trimestreFromDate(f.dateSign);
  const wasabi = f.concept.includes('Wasabi');
  const reprise = wasabi ? 'Oui' : f.reprise;
  const stageProb = (OPP_STAGES5.find((s) => s.key === f.stage) || {}).prob ?? 20;
  const autoName = acc ? `Opportunité - ${(acc.enseigne || acc.name)} ${acc.city || ''}`.trim() + ` - ${new Date().toLocaleDateString('fr-FR')}` : 'Opportunité - …';

  const toggleConcept = (c) => setF((s) => ({ ...s, concept: s.concept.includes(c) ? s.concept.filter((x) => x !== c) : [...s.concept, c] }));
  const toggleSign = (n) => setF((s) => { const has = s.signataires.includes(n); if (has) return { ...s, signataires: s.signataires.filter((x) => x !== n) }; if (s.signataires.length >= 2) { toast.error('2 signataires maximum'); return s; } return { ...s, signataires: [...s.signataires, n] }; });
  const onStagePick = (stage, { declare }) => {
    if (stage === 'perdu' && !f.lostReason) toast('Renseignez la raison de la perte ci-dessous', { icon: '⚠️' });
    setF((s) => ({ ...s, stage, probability: '' }));
    if (stage === 'gagnee' && declare) toast.success('Opportunité gagnée — pensez à déclarer le contrat (module Contrats)');
  };

  const submit = async () => {
    if (!acc) return toast.error('Sélectionnez un compte');
    if (!f.clientExistant) return toast.error('Renseignez « Client existant ? »');
    if (f.stage === 'perdu' && !f.lostReason.trim()) { setPanel(1); return toast.error('Indiquez la raison de la perte'); }
    setBusy(true);
    const prob = f.probability === '' ? stageProb : Math.max(0, Math.min(100, Number(f.probability)));
    try {
      await api().post('/capex-sales/opportunities', {
        account_id: acc.id, name: autoName, nature: nature !== '—' ? nature : null,
        concept: f.concept.join(', ') || null, ca_annuel_previsionnel: f.ca ? Number(f.ca) : null,
        date_cible: f.dateClot || null, owner_name: user?.name || user?.email || null,
        stage: f.stage, probability: prob, status: f.stage === 'perdu' ? 'perdu' : 'open',
        loss_reason: f.stage === 'perdu' ? (f.lostReason || null) : null,
        custom_fields: {
          origine: f.origine || null, client_existant: f.clientExistant, projet: projet !== '—' ? projet : null,
          remodeling: remodeling !== '—' ? remodeling : null, surface_corner: f.surface || null,
          signataires: f.signataires, sign_origine: f.signOrigine || null, demontage: f.demontage || null,
          inv_corner: f.invCorner || null, inv_inox: f.invInox || null, inv_vitrine: f.invVitrine || null,
          inv_wok: f.invWok || null, inv_cellule: f.invCellule || null, wok_a_commander: f.wok || null,
          cellule_a_commander: celluleCmd !== '—' ? celluleCmd : null, ecran_a_commander: f.ecran || null,
          duree_contrat: f.duree || null, taux_commission: f.commission || null, reprise_personnel: reprise || null,
          commentaires: f.commentaires || null, date_dsao: f.dateDsao || null, date_mep: f.dateMep || null,
          date_ouverture: f.dateOuv || null, date_signature: f.dateSign || null, trimestre_signature: trimestre !== '—' ? trimestre : null,
        },
      });
      toast.success('Opportunité créée'); onCreated();
    } catch (e) { toast.error('Erreur à la création'); } finally { setBusy(false); }
  };

  const sel = 'w-full mt-1 px-3 py-2 rounded-lg text-sm';
  const selStyle = { background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--ink)' };
  const autoBox = { ...selStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
  const Radio = ({ id, value, opts, onChange }) => (
    <div className="flex flex-wrap gap-2 mt-1" data-testid={id}>
      {opts.map((o) => <span key={o} onClick={() => onChange(o)} className="cx-chip" style={{ background: value === o ? 'var(--accent)' : 'var(--tile)', color: value === o ? 'var(--on-accent)' : 'var(--ink)', borderColor: 'var(--line-2)', cursor: 'pointer' }} data-testid={`${id}-${o}`}>{o}</span>)}
    </div>
  );
  const Auto = ({ label, value, tid }) => (<div><label className="cx-micro">{label}</label><div className="mt-1 px-3 py-2 rounded-lg text-sm" style={autoBox} data-testid={tid}><span>{value}</span><span className="cx-chip" style={{ background: 'var(--tile)', borderColor: 'var(--line-2)', fontSize: 11 }}>auto</span></div></div>);

  return (
    <div data-testid="cs-opp-create">
      <CxBack label="Retour au pipeline" onClick={onBack} testid="cs-oppcreate-back" />
      <p className="cx-micro" style={{ color: 'var(--accent)', fontWeight: 700, letterSpacing: '.06em' }}>NOUVELLE OPPORTUNITÉ</p>
      <div className="mt-2"><label className="cx-micro">1. Rechercher le compte client ou prospect</label>
        {acc ? (
          <div className="cx-oppitem mt-1" style={{ cursor: 'default', borderColor: 'var(--accent)' }}>
            <div className="flex-1"><div className="font-semibold" style={{ color: 'var(--ink)' }}>{acc.name}</div></div>
            <CxBadge tone={acc.type === 'Client' ? 'client' : 'warn'}>{acc.type}</CxBadge>
            <button className="ml-2" style={{ color: 'var(--accent)', fontWeight: 600, fontSize: 13 }} onClick={() => { setAcc(null); setF((s) => ({ ...s, clientExistant: '' })); }} data-testid="cs-oppcreate-clear">Changer</button>
          </div>
        ) : (
          <>
            <CxSearch value={q} onChange={setQ} placeholder="Nom de l'enseigne + ville…" testid="cs-oppcreate-search" />
            <div className="space-y-2 mt-2">{results.map((a) => (
              <CxItem key={a.id} avatar={initials(a.name)} title={a.name} subtitle={[a.enseigne, a.city].filter(Boolean).join(' · ')}
                right={<CxBadge tone={a.type === 'Client' ? 'client' : 'warn'}>{a.type}</CxBadge>} onClick={() => pickAcc(a)} testid={`cs-oppcreate-pick-${a.id}`} />
            ))}</div>
          </>
        )}
      </div>

      {acc && (
        <>
          <div className="flex gap-2 mt-4 mb-3">
            <button className="cx-actionbtn" style={{ flex: 1, background: panel === 1 ? 'var(--accent)' : 'var(--tile)', color: panel === 1 ? 'var(--on-accent)' : 'var(--ink)', border: '1px solid var(--line-2)' }} onClick={() => setPanel(1)} data-testid="cs-opp-panel-1">1 · Création rapide</button>
            <button className="cx-actionbtn" style={{ flex: 1, background: panel === 2 ? 'var(--accent)' : 'var(--tile)', color: panel === 2 ? 'var(--on-accent)' : 'var(--ink)', border: '1px solid var(--line-2)' }} onClick={() => setPanel(2)} data-testid="cs-opp-panel-2">2 · Dossier technique</button>
          </div>

          {panel === 1 && (
            <div className="space-y-3" data-testid="cs-opp-step1">
              <Auto label="Nom de l'opportunité (automatique)" value={autoName} tid="cs-oppcreate-autoname" />
              <OppStageBar current={f.stage} onPick={onStagePick} />
              {f.stage === 'perdu' && (<div><label className="cx-micro">Raison de la perte</label><textarea className={sel} style={{ ...selStyle, height: 64 }} value={f.lostReason} onChange={(e) => setF({ ...f, lostReason: e.target.value })} placeholder="Motif de la perte…" data-testid="cs-opp-lostreason" /></div>)}
              <div><label className="cx-micro">Concept (plusieurs choix possibles)</label>
                <div className="flex flex-wrap gap-2 mt-1">{OPP_CONCEPTS.map((c) => (
                  <span key={c} onClick={() => toggleConcept(c)} className="cx-chip" style={{ background: f.concept.includes(c) ? 'var(--accent)' : 'var(--tile)', color: f.concept.includes(c) ? 'var(--on-accent)' : 'var(--ink)', borderColor: 'var(--line-2)', cursor: 'pointer' }} data-testid={`cs-oppcreate-concept-${c}`}>{c}</span>
                ))}</div></div>
              <div><label className="cx-micro">Client existant ?</label><Radio id="cs-oppcreate-client" value={f.clientExistant} opts={['Oui', 'Non']} onChange={(v) => setF({ ...f, clientExistant: v })} /></div>
              <Auto label="Projet" value={projet} tid="cs-oppcreate-projet" />
              <div><label className="cx-micro">Origine de la piste</label>
                <select className={sel} style={selStyle} value={f.origine} onChange={(e) => setF({ ...f, origine: e.target.value })} data-testid="cs-oppcreate-origine">
                  <option value="">Choisir l'origine</option>{OPP_ORIGINES.map((x) => <option key={x}>{x}</option>)}
                </select></div>
              <Auto label="Nature du projet" value={nature} tid="cs-oppcreate-nature" />
              <button className="cx-actionbtn primary" onClick={submit} disabled={busy} data-testid="cs-oppcreate-submit">{busy ? 'Création…' : "Enregistrer l'opportunité"}</button>
              <button className="cx-actionbtn ghost" onClick={() => setPanel(2)} data-testid="cs-opp-goto-panel2">Compléter le dossier technique ›</button>
              <button className="cx-actionbtn ghost" onClick={onBack} data-testid="cs-oppcreate-cancel">Annuler</button>
            </div>
          )}

          {panel === 2 && (
            <div className="space-y-3" data-testid="cs-opp-step2">
              <Auto label="Remodeling du corner" value={remodeling} tid="cs-opp-remodel" />
              <div><label className="cx-micro">Surface d'implémentation du corner (m²)</label><input className={sel} style={selStyle} type="number" value={f.surface} onChange={(e) => setF({ ...f, surface: e.target.value })} placeholder="Ex. 14" data-testid="cs-opp-surface" /></div>
              <div><label className="cx-micro">Signataire(s) du contrat</label>
                <MultiSelectSearch options={signataires} value={f.signataires} onChange={(v) => setF({ ...f, signataires: v })} max={2} placeholder="Rechercher un utilisateur…" testid="cs-opp-signataires" />
                <p className="cx-micro mt-1">1 ou 2 signataires maximum.</p></div>
              <div><label className="cx-micro">Origine signature dossier</label>
                <select className={sel} style={selStyle} value={f.signOrigine} onChange={(e) => setF({ ...f, signOrigine: e.target.value })} data-testid="cs-opp-signorigine">
                  <option value="">Choisir l'origine de signature</option>{OPP_SIGN_ORIGINES.map((x) => <option key={x}>{x}</option>)}
                </select></div>
              <div><label className="cx-micro">Prévoir démontage ancien corner</label><Radio id="cs-opp-demontage" value={f.demontage} opts={['Oui', 'Non']} onChange={(v) => setF({ ...f, demontage: v })} /></div>
              <div className="cx-surface p-3" style={{ borderColor: 'var(--accent)' }}>
                <div className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Investissements (CCD ou Client)</div>
                <div className="grid grid-cols-2 gap-3">
                  {[['invCorner', 'Corner'], ['invInox', 'Inox'], ['invVitrine', 'Vitrine réfrigérée'], ['invWok', 'WOK'], ['invCellule', 'Cellule de refroidissement']].map(([k, lb]) => (
                    <div key={k}><div className="cx-micro">{lb}</div><Radio id={`cs-opp-${k}`} value={f[k]} opts={['CCD', 'Client']} onChange={(v) => setF({ ...f, [k]: v })} /></div>
                  ))}
                </div>
              </div>
              <div><label className="cx-micro">Wok à commander</label><Radio id="cs-opp-wok" value={f.wok} opts={['Oui', 'Non']} onChange={(v) => setF({ ...f, wok: v })} /></div>
              <Auto label="Cellule de refroidissement à commander" value={celluleCmd} tid="cs-opp-cellule" />
              <div><label className="cx-micro">Écran d'affichage à commander</label><Radio id="cs-opp-ecran" value={f.ecran} opts={['Oui', 'Non']} onChange={(v) => setF({ ...f, ecran: v })} /></div>
              <div><label className="cx-micro">Durée du contrat</label>
                <select className={sel} style={selStyle} value={f.duree} onChange={(e) => setF({ ...f, duree: e.target.value })} data-testid="cs-opp-duree">{OPP_DUREES.map((x) => <option key={x}>{x}</option>)}</select></div>
              <div><label className="cx-micro">Taux de commission (%)</label><input className={sel} style={selStyle} type="number" min="12" max="25" step="0.1" value={f.commission} onChange={(e) => setF({ ...f, commission: e.target.value })} placeholder="De 12 à 25 (virgule possible)" data-testid="cs-opp-commission" /></div>
              <div><label className="cx-micro">Reprise du personnel</label><Radio id="cs-opp-reprise" value={reprise} opts={['Oui', 'Non']} onChange={(v) => !wasabi && setF({ ...f, reprise: v })} /><p className="cx-micro mt-1">Personnel Wasabi repris automatiquement si le concept Wasabi est sélectionné.</p></div>
              <div><label className="cx-micro">Commentaires</label><textarea className={sel} style={{ ...selStyle, height: 64 }} value={f.commentaires} onChange={(e) => setF({ ...f, commentaires: e.target.value })} placeholder="Texte libre…" data-testid="cs-opp-commentaires" /></div>
              <div className="cx-surface p-3">
                <div className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Dates clés</div>
                <div className="grid grid-cols-2 gap-3">
                  {[['dateDsao', 'Installation DSAO'], ['dateMep', 'MEP (ETP)'], ['dateOuv', 'Ouverture prévisionnelle corner'], ['dateSign', 'Signature du contrat']].map(([k, lb]) => (
                    <div key={k}><label className="cx-micro">{lb}</label><input type="date" className={sel} style={selStyle} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`cs-opp-${k}`} /></div>
                  ))}
                </div>
              </div>
              <Auto label="Trimestre de signature" value={trimestre} tid="cs-opp-trimestre" />
              <div className="cx-surface p-3">
                <div className="font-semibold mb-2" style={{ color: 'var(--accent)' }}>Suivi pipeline</div>
                <div className="space-y-3">
                  <div><label className="cx-micro">CA annuel prévisionnel (€)</label><input className={sel} style={selStyle} type="number" value={f.ca} onChange={(e) => setF({ ...f, ca: e.target.value })} placeholder="Ex. 410 000" data-testid="cs-opp-ca" /></div>
                  <div><label className="cx-micro">Probabilité (%)</label><input className={sel} style={selStyle} type="number" min="0" max="100" value={f.probability} onChange={(e) => setF({ ...f, probability: e.target.value })} placeholder={`Se remplit selon l'étape (${stageProb} %)`} data-testid="cs-opp-probability" /></div>
                  <div><label className="cx-micro">Date de clôture prévue</label><input type="date" className={sel} style={selStyle} value={f.dateClot} onChange={(e) => setF({ ...f, dateClot: e.target.value })} data-testid="cs-opp-dateclot" /></div>
                </div>
              </div>
              <button className="cx-actionbtn primary" onClick={submit} disabled={busy} data-testid="cs-oppcreate-submit2">{busy ? 'Enregistrement…' : 'Enregistrer'}</button>
              <button className="cx-actionbtn ghost" onClick={() => setPanel(1)} data-testid="cs-opp-back-panel1">‹ Retour aux informations</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const CR_ORIGINE = ['Sur RDV', 'Sur RDV Télépro', 'Sans RDV'];
const CR_OBJET = ['Prospection', 'Renouvellement', 'Suivi client', 'Suivi post installation', 'Visite sur salon', 'Parrainage', 'Visite technique', 'Visite courtoisie', "Info apporteur d'affaires"];
const CR_RESULTAT = ['Prise de RDV', "Prise d'infos client", 'Évaluation potentiel magasin', 'Présentation produits', 'Argumentation', 'Contrat laissé', 'Contrat signé', 'Formation produit', 'Dépannage technique', 'Récupération 06', 'Suivi installation', 'RDV annulé', 'Aucun intérêt du client'];
const CR_SONCAS = ['Pas d\'avis', 'Sécurité', 'Orgueil', 'Nouveautés', 'Confort', 'Argent', 'Sympathie'];
const CR_CONCEPTION = ['3 faces', '4 faces', 'Dans les trad', 'En bergerie'];
const CR_ZONE = ['On va tout casser', 'Bonne', 'Moyenne', 'Nulle'];

const selCls = 'w-full mt-1 px-3 py-2 rounded-lg text-sm';
const selSt = { background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--ink)' };
function Chip({ on, onClick, children, testid }) {
  return <span onClick={onClick} data-testid={testid} className="cx-chip" style={{ cursor: 'pointer', background: on ? 'var(--accent)' : 'var(--tile)', color: on ? 'var(--on-accent)' : 'var(--ink)', borderColor: 'var(--line-2)' }}>{children}</span>;
}

function ComptesRendus({ api, user, onBack }) {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const [openCr, setOpenCr] = useState(null);
  const [period, setPeriod] = useState('all');
  const [ownerF, setOwnerF] = useState('');
  const [sort, setSort] = useState('recent');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showCounts, setShowCounts] = useState(false);
  const [search, setSearch] = useState('');
  const isAdmin = ['super_admin', 'admin'].includes(user?.role) || (user?.email || '').toLowerCase() === 'c.perez@wasabicorner.fr';
  const load = useCallback(() => { api().get('/capex-sales/activities').then((r) => setItems((r.data.items || []).filter((a) => a.type === 'Compte-rendu'))).catch(() => setItems([])); }, [api]);
  useEffect(() => { load(); }, [load]);
  if (creating) return <CRForm api={api} user={user} onBack={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />;
  if (openCr) return <CrDetail cr={openCr} onBack={() => setOpenCr(null)} />;

  const list = items || [];
  const owners = Array.from(new Set(list.map((a) => a.owner_name).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  const now = new Date();
  const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - day); return x; };
  const inPeriod = (iso) => {
    if (period === 'all') return true;
    if (!iso) return false;
    const d = new Date(iso); if (isNaN(d.getTime())) return false;
    if (period === 'semaine') return d >= startOfWeek(now);
    if (period === 's1') { const s = startOfWeek(now); const s1 = new Date(s); s1.setDate(s.getDate() - 7); return d >= s1 && d < s; }
    if (period === 'mois') return d >= new Date(now.getFullYear(), now.getMonth(), 1);
    if (period === 'trimestre') return d >= new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    if (period === 'annee') return d >= new Date(now.getFullYear(), 0, 1);
    if (period === 'perso') { const f = from ? new Date(from) : null; const t = to ? new Date(to + 'T23:59:59') : null; if (f && d < f) return false; if (t && d > t) return false; return true; }
    return true;
  };
  const monthsSince = (iso) => { if (!iso) return null; const d = new Date(iso); if (isNaN(d.getTime())) return null; return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24 * 30.44); };
  const dot = (iso) => { const m = monthsSince(iso); if (m === null) return '#6b7280'; if (m > 3) return '#e0563b'; if (m < 0.25) return '#7dc242'; return '#3b82f6'; };
  let filtered = list.filter((a) => inPeriod(a.created_at) && (!ownerF || a.owner_name === ownerF));
  if (search.trim()) {
    const q = search.trim().toLowerCase();
    filtered = filtered.filter((a) => `${a.related_name || ''} ${a.subject || ''}`.toLowerCase().includes(q));
  }
  filtered = [...filtered].sort((a, b) => {
    if (sort === 'owner') return (a.owner_name || '').localeCompare(b.owner_name || '');
    const da = new Date(a.created_at || 0).getTime(), dbb = new Date(b.created_at || 0).getTime();
    return sort === 'ancien' ? da - dbb : dbb - da;
  });
  const anyFilter = period !== 'all' || ownerF || sort !== 'recent' || search.trim();
  const periodList = list.filter((a) => inPeriod(a.created_at));
  const counts = {};
  periodList.forEach((a) => { const o = a.owner_name || '—'; counts[o] = (counts[o] || 0) + 1; });
  const countRows = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const exportCr = async () => {
    try {
      const res = await api().get('/capex-sales/comptes-rendus/export', { params: { period, owner: ownerF, date_from: from, date_to: to }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a'); link.href = url; link.download = `comptes_rendus_${period}.xlsx`; document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export impossible'); }
  };
  const selSt = { background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--ink)', borderRadius: 10, padding: '6px 8px', fontSize: 13, width: '100%', marginTop: 2 };

  return (
    <div data-testid="cs-cr">
      <CxBack label="Retour à l'accueil" onClick={onBack} testid="cs-back" />
      <div className="flex items-center justify-between mb-4">
        <h1 className="cx-h1">Comptes rendus</h1>
        <span className="text-xs cx-muted-3">{user?.name || user?.email}</span>
      </div>
      <div className="flex gap-2">
        <button className="cx-actionbtn primary" style={{ flex: 1 }} onClick={() => setCreating(true)} data-testid="cs-cr-new"><Plus className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Nouveau compte rendu</button>
        <button className="cx-actionbtn ghost" onClick={exportCr} data-testid="cs-cr-export"><Upload className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Export Excel</button>
      </div>

      <div className="cx-surface p-3 mt-4" data-testid="cs-cr-filters">
        <div className="flex items-center justify-between mb-2"><span className="cx-micro" style={{ fontWeight: 700, letterSpacing: '.05em' }}>FILTRES</span>{anyFilter && <button className="cx-micro" style={{ color: 'var(--accent)', fontWeight: 700 }} onClick={() => { setPeriod('all'); setOwnerF(''); setSort('recent'); setFrom(''); setTo(''); setSearch(''); }} data-testid="cs-cr-filters-reset">Réinitialiser</button>}</div>
        <div className={`grid ${isAdmin ? 'grid-cols-2' : 'grid-cols-2'} gap-2`}>
          <div><label className="cx-micro">Période</label>
            <select value={period} onChange={(e) => setPeriod(e.target.value)} style={selSt} data-testid="cs-cr-filter-period">
              {[['all', 'Toutes'], ['semaine', 'Cette semaine'], ['s1', 'Semaine dernière (S-1)'], ['mois', 'Mois en cours'], ['trimestre', 'Trimestre'], ['annee', 'Année'], ['perso', 'Période spécifique']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          <div><label className="cx-micro">Trier par</label>
            <select value={sort} onChange={(e) => setSort(e.target.value)} style={selSt} data-testid="cs-cr-filter-sort">
              {[['recent', 'Date (récent)'], ['ancien', 'Date (ancien)'], ['owner', 'Commercial (A→Z)']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></div>
          {isAdmin && (
            <div className="col-span-2"><label className="cx-micro">Commercial (propriétaire)</label>
              <select value={ownerF} onChange={(e) => setOwnerF(e.target.value)} style={selSt} data-testid="cs-cr-filter-owner">
                <option value="">Tous les commerciaux</option>{owners.map((o) => <option key={o} value={o}>{o}</option>)}
              </select></div>
          )}
          {period === 'perso' && (
            <>
              <div><label className="cx-micro">Du</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={selSt} data-testid="cs-cr-filter-from" /></div>
              <div><label className="cx-micro">Au</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={selSt} data-testid="cs-cr-filter-to" /></div>
            </>
          )}
        </div>
      </div>

      {isAdmin && (
        <>
          <button type="button" onClick={() => setShowCounts((v) => !v)} data-testid="cs-cr-counts-tile"
            className="w-full mt-4 flex items-center gap-3 p-3 rounded-xl"
            style={{ background: 'var(--bg-2)', border: `1px solid ${showCounts ? 'var(--accent)' : 'var(--line-2)'}`, cursor: 'pointer', textAlign: 'left' }}>
            <span className="cx-tile__icon" style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0 }}><BarChart3 className="w-5 h-5" style={{ color: 'var(--accent)' }} /></span>
            <span className="flex-1 min-w-0">
              <span className="block font-bold" style={{ color: 'var(--ink)', fontSize: 15 }}>Nombre de CR{period !== 'all' ? ' (période)' : ''}</span>
              <span className="block text-xs cx-muted">Répartition par commercial · cliquer pour {showCounts ? 'masquer' : 'afficher'}</span>
            </span>
            <span className="cx-num font-bold" style={{ color: 'var(--accent)', fontSize: 22 }}>{periodList.length}</span>
            <span className="cx-muted flex-shrink-0 ml-1" style={{ transform: showCounts ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>›</span>
          </button>
          {showCounts && countRows.length > 0 && (
            <div className="cx-surface p-3 mt-2" data-testid="cs-cr-counts">
              <div className="cx-micro mb-2" style={{ fontWeight: 700, letterSpacing: '.05em' }}>CR PAR COMMERCIAL{period !== 'all' ? ' (période)' : ''} · {periodList.length} au total · cliquer pour filtrer</div>
              <div className="space-y-1" style={{ maxHeight: '46vh', overflowY: 'auto' }}>{countRows.map(([o, n]) => {
                const activeRow = ownerF && o === ownerF;
                return (
                <button type="button" key={o} onClick={() => { const next = (o === '—' || activeRow) ? '' : o; setOwnerF(next); setShowCounts(false); }}
                  className="w-full flex items-center justify-between text-sm py-1 px-2 rounded-lg" data-testid={`cs-cr-count-${o}`}
                  style={{ background: activeRow ? 'rgba(239,118,35,.14)' : 'transparent', border: `1px solid ${activeRow ? 'var(--accent)' : 'transparent'}`, cursor: 'pointer', textAlign: 'left' }}>
                  <span className="truncate" style={{ color: activeRow ? 'var(--accent)' : 'var(--ink)', fontWeight: activeRow ? 700 : 500 }}>{o}</span>
                  <span className="cx-num font-bold ml-2" style={{ color: 'var(--accent)' }}>{n}</span>
                </button>
                );
              })}</div>
            </div>
          )}
        </>
      )}

      <div className="cx-search mt-4" data-testid="cs-cr-search-wrap">
        <span className="cx-search__icon"><SearchIcon className="w-4 h-4" /></span>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un client ou prospect…" data-testid="cs-cr-search" />
      </div>

      <div className="flex items-center justify-between mt-4 mb-2">
        <p className="cx-micro" style={{ margin: 0 }}>Comptes rendus ({filtered.length}{anyFilter ? ` / ${list.length}` : ''})</p>
        {ownerF && <button className="cx-micro" style={{ color: 'var(--accent)', fontWeight: 700 }} onClick={() => setOwnerF('')} data-testid="cs-cr-owner-clear">✕ {ownerF}</button>}
      </div>
      {items === null ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>
        : filtered.length === 0 ? <CxEmpty>{anyFilter ? 'Aucun compte rendu pour ces filtres.' : 'Aucun compte rendu. Créez-en un depuis le terrain.'}</CxEmpty>
          : <div className="space-y-2 pr-1" style={{ maxHeight: '62vh', overflowY: 'auto' }} data-testid="cs-cr-list-scroll">{filtered.map((a) => {
            const clientName = a.related_name || a.subject;
            const objet = a.subject && a.subject !== a.related_name ? a.subject.replace(a.related_name || '', '').replace(/^\s*—\s*/, '').trim() || a.subject : '';
            const sub = [objet, cleanCrBody(a.body, a.custom_fields)].filter(Boolean).join(' · ');
            return (
            <div key={a.id} className="cx-oppitem" onClick={() => setOpenCr(a)} style={{ cursor: 'pointer', alignItems: 'flex-start' }} data-testid={`cs-cr-row-${a.id}`}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: dot(a.created_at), flexShrink: 0, marginTop: 6 }} title="Fraîcheur du CR" />
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate mb-1" style={{ color: 'var(--ink)', fontSize: 16 }} data-testid={`cs-cr-client-${a.id}`}>{clientName}</div>
                <div className="flex items-center gap-2 flex-wrap mb-1"><CrOwnerChip name={a.owner_name} testid={`cs-cr-list-owner-${a.id}`} /><span className="text-xs" style={{ color: 'var(--ink-3)', fontWeight: 600 }}>{fmtFrDate(a.created_at)}</span></div>
                {sub ? <div className="text-xs truncate cx-muted">{sub.slice(0, 90)}</div> : null}
              </div>
              <span className="cx-muted flex-shrink-0">›</span>
            </div>
            );
          })}</div>}
    </div>
  );
}

function CRForm({ api, user, onBack, onSaved, presetStore }) {
  const [q, setQ] = useState(''); const [results, setResults] = useState([]); const [store, setStore] = useState(presetStore || null);
  const [f, setF] = useState({ contact: '', origine: '', objet: '', resultat: '', soncas: [], vip: 'Non', interet: '', conception: '', zone: '', notes: '' });
  const [dd, setDd] = useState({ contact_rencontre: [], origine: [], objet: [], resultat: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api().get('/capex-sales/cr-dropdowns').then((r) => setDd(r.data.lists || {})).catch(() => {}); }, [api]);
  useEffect(() => { const t = setTimeout(() => { if (q.trim().length >= 2) api().get('/capex-sales/accounts', { params: { q, limit: 8 } }).then((r) => setResults(r.data.items || [])); else setResults([]); }, 250); return () => clearTimeout(t); }, [q, api]);
  const tog = (k, v) => setF((s) => ({ ...s, [k]: s[k] === v ? '' : v }));
  const togMulti = (v) => setF((s) => ({ ...s, soncas: s.soncas.includes(v) ? s.soncas.filter((x) => x !== v) : [...s.soncas, v] }));
  const save = async (draft) => {
    if (!store) return toast.error('Sélectionnez un magasin');
    setBusy(true);
    const parts = [f.contact && `Contact : ${f.contact}`, f.origine && `Origine : ${f.origine}`, f.resultat && `Résultat : ${f.resultat}`, f.soncas.length && `SONCAS : ${f.soncas.join(', ')}`, f.interet && `Intérêt corner : ${f.interet}`, f.conception && `Conception : ${f.conception}`, f.zone && `Zone : ${f.zone}`, f.notes].filter(Boolean);
    try {
      await api().post('/capex-sales/activities', {
        type: 'Compte-rendu', subject: `${store.name} — ${f.objet || 'Visite'}${draft ? ' (brouillon)' : ''}`,
        body: parts.join(' · '), related_type: 'account', related_id: store.id, related_name: store.name,
        owner_name: user?.name || user?.email || null,
      });
      toast.success(draft ? 'Brouillon enregistré' : 'Compte rendu enregistré'); onSaved();
    } catch (e) { toast.error('Erreur'); } finally { setBusy(false); }
  };
  return (
    <div data-testid="cs-cr-form">
      <CxBack label="Retour aux comptes rendus" onClick={onBack} testid="cs-cr-back" />
      <h1 className="cx-h1 mb-3">Nouveau compte rendu</h1>
      {store ? (
        <div className="cx-oppitem" style={{ cursor: 'default' }}><div className="flex-1"><div className="font-semibold" style={{ color: 'var(--ink)' }}>{store.name}</div><div className="text-xs cx-muted">{[store.city, store.cp].filter(Boolean).join(' ')}</div></div><button className="cx-stepbtn" onClick={() => setStore(null)}>×</button></div>
      ) : (<>
        <CxSearch value={q} onChange={setQ} placeholder="Sélectionner un magasin…" testid="cs-cr-store-search" />
        <div className="space-y-2 mt-2">{results.map((a) => <CxItem key={a.id} avatar={initials(a.name)} title={a.name} subtitle={[a.city, a.type].filter(Boolean).join(' · ')} onClick={() => { setStore(a); setResults([]); setQ(''); }} testid={`cs-cr-store-${a.id}`} />)}</div>
      </>)}
      {store && (
        <div className="mt-4 space-y-3">
          {[['contact', 'Contact rencontré', dd.contact_rencontre || []], ['origine', 'Origine', dd.origine || []], ['objet', 'Objet', dd.objet || []], ['resultat', 'Résultat', dd.resultat || []]].map(([k, lb, opts]) => (
            <div key={k}><label className="cx-micro">{lb}</label>
              <select className={selCls} style={selSt} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`cs-cr-${k}`}>
                <option value="">Choisir</option>{opts.map((o) => <option key={o}>{o}</option>)}
              </select></div>
          ))}
          <div><p className="cx-micro mb-1">Mon client il est · SONCAS</p><div className="flex flex-wrap gap-2">{CR_SONCAS.map((s) => <Chip key={s} on={f.soncas.includes(s)} onClick={() => togMulti(s)} testid={`cs-cr-soncas-${s}`}>{s}</Chip>)}</div></div>
          <div><p className="cx-micro mb-1">Intérêt pour un corner</p><div className="flex gap-2">{['Oui', 'Non'].map((v) => <Chip key={v} on={f.interet === v} onClick={() => tog('interet', v)} testid={`cs-cr-interet-${v}`}>{v}</Chip>)}</div></div>
          <div><p className="cx-micro mb-1">Conception</p><div className="flex flex-wrap gap-2">{CR_CONCEPTION.map((v) => <Chip key={v} on={f.conception === v} onClick={() => tog('conception', v)}>{v}</Chip>)}</div></div>
          <div><p className="cx-micro mb-1">Zone</p><div className="flex flex-wrap gap-2">{CR_ZONE.map((v) => <Chip key={v} on={f.zone === v} onClick={() => tog('zone', v)}>{v}</Chip>)}</div></div>
          <div><label className="cx-micro">Notes de terrain</label><textarea className={selCls} style={{ ...selSt, height: 80 }} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Observations, points à suivre…" data-testid="cs-cr-notes" /></div>
          <button className="cx-actionbtn primary" onClick={() => save(false)} disabled={busy} data-testid="cs-cr-save">{busy ? 'Enregistrement…' : 'Enregistrer le compte rendu'}</button>
          <button className="cx-actionbtn ghost" onClick={() => save(true)} disabled={busy}>Enregistrer en brouillon</button>
        </div>
      )}
    </div>
  );
}

const CR_ACTIVE = ['Signé', 'Actif', 'À renouveler'];
function Contrats({ api, user, onBack }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api().get('/capex-sales/contracts').then((r) => setData(r.data.items || [])).catch(() => setData([])); }, [api]);
  useEffect(() => { load(); }, [load]);
  const act = async (c, status) => { try { await api().patch(`/capex-sales/contracts/${c.id}`, { status }); toast.success(status === 'Refusé' ? 'Contrat refusé' : 'Contrat validé'); load(); } catch (e) { toast.error('Erreur'); } };
  if (data === null) return <div className="py-24 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>;
  const sas = data.filter((c) => c.status === 'À valider');
  const actifs = data.filter((c) => CR_ACTIVE.includes(c.status));
  return (
    <div data-testid="cs-contrats">
      <CxBack label="Retour à l'accueil" onClick={onBack} testid="cs-back" />
      <div className="flex items-center justify-between mb-4"><h1 className="cx-h1">Contrats</h1><span className="text-xs cx-muted-3">Validation &amp; suivi</span></div>
      <div className="cx-kpis">
        <div className="cx-kpi"><div className="v">{sas.length}</div><div className="l">Sas de vérification</div></div>
        <div className="cx-kpi"><div className="v">{actifs.length}</div><div className="l">Contrats actifs</div></div>
      </div>
      <p className="text-xs cx-muted mb-4" style={{ lineHeight: 1.5 }}>Un contrat ne se crée que depuis une opportunité gagnée. Les contrats déclarés par les commerciaux arrivent ici pour validation avant de devenir actifs.</p>
      <p className="cx-micro mb-2">Sas de vérification ({sas.length})</p>
      {sas.length === 0 ? <CxEmpty>Aucun contrat en attente de validation.</CxEmpty> : (
        <div className="space-y-2 mb-5">{sas.map((c) => (
          <div key={c.id} className="p-3 cx-surface" data-testid={`cs-contract-sas-${c.id}`}>
            <div className="flex items-center gap-3"><FileText className="w-5 h-5" style={{ color: '#9aa6f2' }} />
              <div className="flex-1 min-w-0"><div className="font-semibold truncate" style={{ color: 'var(--ink)' }}>{c.account_name || c.title}</div><div className="text-xs cx-muted truncate">{c.title} · {c.notes || 'déclaré'}</div></div>
              <CxBadge tone="warn">À valider</CxBadge></div>
            <div className="flex gap-2 mt-3">
              <button className="cx-actionbtn green" style={{ marginTop: 0 }} onClick={() => act(c, 'Actif')} data-testid={`cs-contract-accept-${c.id}`}>Accepter</button>
              <button className="cx-actionbtn ghost" style={{ marginTop: 0 }} onClick={() => act(c, 'Refusé')} data-testid={`cs-contract-refuse-${c.id}`}>Refuser</button>
            </div>
          </div>
        ))}</div>
      )}
      <p className="cx-micro mb-2">Contrats actifs</p>
      {actifs.length === 0 ? <CxEmpty>Aucun contrat actif.</CxEmpty> : (
        <div className="space-y-2">{actifs.map((c) => (
          <CxItem key={c.id} avatar={initials(c.account_name || c.title)} title={c.account_name || c.title}
            subtitle={[c.title, c.signed_date && ('Signé ' + c.signed_date.slice(0, 10))].filter(Boolean).join(' · ')}
            right={<CxBadge tone={c.status === 'À renouveler' ? 'warn' : 'client'}>{c.status}</CxBadge>} testid={`cs-contract-actif-${c.id}`} />
        ))}</div>
      )}
    </div>
  );
}


function SuiviChiffre({ api, user, onBack }) {
  const [d, setD] = useState(null);
  const [showDataDir, setShowDataDir] = useState(false);
  const isAdmin = ['admin', 'super_admin'].includes(user?.role) || (user?.email || '').toLowerCase() === 'c.perez@wasabicorner.fr';
  useEffect(() => { api().get('/capex-sales/suivi-chiffre').then((r) => setD(r.data)).catch(() => setD(null)); }, [api]);
  if (showDataDir) return <DataDirection api={api} user={user} onBack={() => setShowDataDir(false)} />;
  if (!d) return <div className="py-24 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>;
  const medal = ['#e0b45a', '#c0c6d0', '#cd7f45'];
  return (
    <div data-testid="cs-chiffre">
      <CxBack label="Retour à l'accueil" onClick={onBack} testid="cs-back" />
      <div className="flex items-center justify-between mb-4"><h1 className="cx-h1">Suivi du Chiffre</h1><span className="text-xs cx-muted-3">Mois {d.month}</span></div>
      <p className="text-xs cx-muted mb-4">Tableau transparent : chaque commercial voit l'activité de l'équipe.</p>
      {isAdmin && (
        <button onClick={() => setShowDataDir(true)} data-testid="cs-datadir-open"
          className="w-full mb-4 flex items-center gap-3 p-3 rounded-xl"
          style={{ background: 'var(--bg-2)', border: '1px solid var(--accent)', cursor: 'pointer', textAlign: 'left' }}>
          <span className="cx-tile__icon" style={{ width: 40, height: 40, borderRadius: 12, flexShrink: 0 }}><PieChart className="w-5 h-5" style={{ color: 'var(--accent)' }} /></span>
          <span className="flex-1 min-w-0">
            <span className="block font-bold text-sm" style={{ color: 'var(--ink)' }}>DATA DIRECTION</span>
            <span className="block text-xs cx-muted">Indicateurs de pilotage</span>
          </span>
          <span className="text-[10px] font-bold px-2 py-1 rounded" style={{ background: 'rgba(224,180,90,.15)', color: '#e0b45a', border: '1px solid rgba(224,180,90,.4)' }}>ADMIN</span>
        </button>
      )}
      <div className="cx-tranche-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        {d.counters.map((c) => (
          <div key={c.label} className="cx-kpi" data-testid={`cs-counter-${c.label}`}>
            <div className="v">{c.value}</div>
            <div className="l">{c.label}</div>
            <div className="text-xs mt-1" style={{ color: c.trend >= 0 ? 'var(--green)' : 'var(--alert)' }}>{c.trend >= 0 ? '▲ +' : '▼ '}{c.trend} vs M-1</div>
          </div>
        ))}
      </div>
      <div className="cx-kpis mt-3">
        <div className="cx-kpi"><div className="v">{d.opportunities_open}</div><div className="l">Opportunités ouvertes</div></div>
        <div className="cx-kpi"><div className="v">{fmtK(d.forecast)}</div><div className="l">Prévisionnel pondéré</div></div>
      </div>
      <p className="cx-micro mt-5 mb-2 flex items-center gap-1"><Trophy className="w-4 h-4" style={{ color: '#e0b45a' }} /> Podium du mois (comptes rendus)</p>
      {d.podium.length === 0 ? <CxEmpty>Pas encore d'activité ce mois-ci.</CxEmpty> : (
        <div className="space-y-2">{d.podium.map((p, i) => (
          <div key={p.name} className="cx-oppitem" style={{ cursor: 'default' }} data-testid={`cs-podium-${i}`}>
            <span className="cx-pct" style={{ color: medal[i] || 'var(--ink-3)', borderColor: medal[i] || 'var(--line-2)', fontSize: 16 }}>{i + 1}</span>
            <div className="flex-1 min-w-0"><div className="font-semibold truncate" style={{ color: 'var(--ink)' }}>{p.name}</div><div className="text-xs cx-muted">{p.score} compte(s) rendu(s)</div></div>
            {i === 0 && <Trophy className="w-5 h-5" style={{ color: '#e0b45a' }} />}
          </div>
        ))}</div>
      )}
    </div>
  );
}

function DataDirection({ api, user, onBack }) {
  const [period, setPeriod] = useState('annee');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [indicator, setIndicator] = useState(null);
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openAccountId, setOpenAccountId] = useState(null);
  const PAGE_SIZE = 25;
  const TILES_DD = [
    { key: 'signatures', label: 'Signatures prospects' },
    { key: 'renouvellements', label: 'Renouvellements clients' },
    { key: 'ouvertures', label: 'Ouvertures' },
    { key: 'opportunites', label: 'Opportunités créées' },
  ];
  const PERIODS = [
    { key: 'mensuel', label: 'Mensuel' }, { key: 'trimestre', label: 'Trimestre' },
    { key: 'annee', label: 'Année civile' }, { key: 'custom', label: 'Personnalisé' },
  ];
  useEffect(() => {
    setLoading(true);
    const params = { period, page, page_size: PAGE_SIZE };
    if (indicator) params.indicator = indicator;
    if (period === 'custom') { params.date_from = from; params.date_to = to; }
    api().get('/capex-sales/data-direction', { params })
      .then((r) => setD(r.data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [api, period, from, to, indicator, page]);

  const fmtDate = (iso) => { try { const [y, m, dd] = iso.split('-'); return `${dd}/${m}/${y}`; } catch { return iso; } };
  const selectTile = (k) => { if (indicator === k) { setIndicator(null); } else { setIndicator(k); setPage(1); } };
  const changePeriod = (k) => { setPeriod(k); setPage(1); };

  if (openAccountId) return <AccountFiche api={api} user={user} kind="Client" id={openAccountId} onBack={() => setOpenAccountId(null)} onConverted={() => setOpenAccountId(null)} />;

  const counts = d?.counts || {};
  const sums = d?.sums || {};
  const detail = d?.detail;
  const totalPages = detail ? Math.max(1, Math.ceil(detail.total / detail.page_size)) : 1;

  const exportXlsx = async () => {
    try {
      const params = { indicator: indicator || 'signatures', period };
      if (period === 'custom') { params.date_from = from; params.date_to = to; }
      const res = await api().get('/capex-sales/data-direction/export', { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `data_direction_${params.indicator}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Échec de l'export"); }
  };

  return (
    <div data-testid="cs-datadir">
      <CxBack label="Retour au Suivi du Chiffre" onClick={onBack} testid="cs-datadir-back" />
      <h1 className="cx-h1">Data Direction</h1>
      <div className="text-xs cx-muted mb-3">Indicateurs de pilotage</div>
      <div className="inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-lg" style={{ background: 'rgba(224,180,90,.12)', border: '1px solid rgba(224,180,90,.4)' }}>
        <span className="text-[11px] font-bold tracking-wide" style={{ color: '#e0b45a' }}>🔒 RÉSERVÉ ADMIN &amp; SUPER ADMIN</span>
      </div>
      <p className="text-xs cx-muted mb-4">Source : champs des comptes clients et des opportunités. Choisissez une période, puis un indicateur pour afficher le détail des dossiers.</p>

      <div className="flex flex-wrap gap-2 mb-2">
        {PERIODS.map((p) => (
          <span key={p.key} onClick={() => changePeriod(p.key)} className="cx-chip" data-testid={`cs-datadir-period-${p.key}`}
            style={{ cursor: 'pointer', background: period === p.key ? 'var(--accent)' : 'var(--tile)', color: period === p.key ? 'var(--on-accent)' : 'var(--ink)', borderColor: 'var(--line-2)', fontWeight: period === p.key ? 700 : 500 }}>
            {p.label}
          </span>
        ))}
      </div>
      {period === 'custom' && (
        <div className="flex gap-2 mb-2" data-testid="cs-datadir-custom">
          <div className="flex-1"><label className="cx-label">Du</label><input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} data-testid="cs-datadir-from" style={{ background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--ink)', borderRadius: 8, padding: '7px 10px', fontSize: 13, width: '100%' }} /></div>
          <div className="flex-1"><label className="cx-label">Au</label><input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} data-testid="cs-datadir-to" style={{ background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--ink)', borderRadius: 8, padding: '7px 10px', fontSize: 13, width: '100%' }} /></div>
        </div>
      )}
      <div className="text-xs cx-muted-3 mb-3">Période : {d?.period_label || '…'}</div>

      <div className="cx-tranche-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
        {TILES_DD.map((t) => {
          const active = indicator === t.key;
          return (
            <button key={t.key} onClick={() => selectTile(t.key)} data-testid={`cs-datadir-tile-${t.key}`}
              className="cx-kpi" style={{ cursor: 'pointer', textAlign: 'center', border: active ? '1px solid var(--accent)' : '1px solid var(--line)', background: active ? 'rgba(240,110,42,.08)' : 'var(--bg-2)' }}>
              <div className="v" style={{ fontSize: 34 }}>{loading && !d ? '…' : (counts[t.key] ?? 0)}</div>
              <div className="l">{t.label}</div>
              {(t.key === 'signatures' || t.key === 'renouvellements' || t.key === 'opportunites') && (
                <div className="text-xs mt-0.5" style={{ color: 'var(--ink-3)' }}>CA prév. {(() => { const v = sums[t.key] || 0; return v >= 1000000 ? (v / 1000000).toFixed(1).replace('.', ',') + ' M€' : Math.round(v / 1000) + ' k€'; })()}</div>
              )}
              <div className="text-xs mt-1 font-semibold" style={{ color: 'var(--accent)' }}>Voir le détail ›</div>
            </button>
          );
        })}
      </div>

      {indicator && detail && (
        <div className="mt-5" data-testid="cs-datadir-detail">
          <div className="flex items-center justify-between mb-2">
            <span className="cx-micro" style={{ color: 'var(--accent)' }}>{TILES_DD.find((t) => t.key === indicator)?.label.toUpperCase()}</span>
            <div className="flex items-center gap-3">
              <button onClick={exportXlsx} className="text-xs font-semibold inline-flex items-center gap-1" style={{ color: 'var(--accent)' }} data-testid="cs-datadir-export"><Upload className="w-3.5 h-3.5" /> Exporter Excel</button>
              <span className="text-xs cx-muted-3">{detail.total} DOSSIER{detail.total > 1 ? 'S' : ''}</span>
            </div>
          </div>
          {detail.total === 0 ? <CxEmpty>Aucun dossier sur la période.</CxEmpty> : (
            <>
              <div className="space-y-2">
                {detail.items.map((r) => (
                  <button key={r.type + r.id} onClick={() => r.account_id && setOpenAccountId(r.account_id)} data-testid={`cs-datadir-row-${r.id}`}
                    className="w-full text-left flex items-center gap-3 p-3 cx-surface" style={{ cursor: r.account_id ? 'pointer' : 'default' }}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate flex items-center gap-1.5" style={{ color: 'var(--ink)' }}>
                        {r.account_name || '—'}
                        {r.is_remodeling && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: 'rgba(192,198,208,.18)', color: '#c0c6d0', border: '1px solid rgba(192,198,208,.35)' }}>Remodeling</span>}
                      </div>
                      <div className="text-xs cx-muted truncate">{fmtDate(r.pivot_date)} · {r.owner_name || '—'}</div>
                    </div>
                    <div className="font-bold flex-shrink-0" style={{ color: r.value_kind === 'ca' ? 'var(--ink)' : r.value_kind === 'status' ? 'var(--green)' : 'var(--accent)' }}>{r.value_label}</div>
                  </button>
                ))}
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-3">
                  <button className="cx-actionbtn ghost" style={{ marginTop: 0, width: 'auto', padding: '0 14px', opacity: page <= 1 ? 0.4 : 1 }} disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="cs-datadir-prev">‹ Précédent</button>
                  <span className="text-xs cx-muted-3">Page {detail.page} / {totalPages}</span>
                  <button className="cx-actionbtn ghost" style={{ marginTop: 0, width: 'auto', padding: '0 14px', opacity: page >= totalPages ? 0.4 : 1 }} disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="cs-datadir-next">Suivant ›</button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const CS_ACTION_TYPES = ['Faire appel', 'Envoyer un mail', 'Envoyer proposition commerciale', 'RDV à poser', 'Etude Capex', 'Envoyer contrat', 'Demande de plan DSAO', 'Participation ouverture', 'Suivi client post ouverture'];
const overdueDays = (due) => { if (!due) return null; const d = new Date(String(due).slice(0, 10)); if (isNaN(d.getTime())) return null; const diff = Math.floor((Date.now() - d.getTime()) / 86400000); return diff > 0 ? diff : 0; };
const actionBucket = (days) => { if (days == null) return null; if (days >= 15 && days <= 30) return '15'; if (days >= 31 && days <= 45) return '1m'; if (days > 45) return 'urgence'; return null; };
const CS_BUCKETS = [
  { key: '15', label: '+ 15 jours', sub: '15 à 30 j', color: '#e0b45a' },
  { key: '1m', label: '+ 1 mois', sub: '31 à 45 j', color: '#ef7623' },
  { key: 'urgence', label: 'URGENCE', sub: '+ de 45 j', color: '#eb563b' },
];

function ActionsMener({ api, user, onBack, presetStore }) {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(!!presetStore);
  const [nt, setNt] = useState({ subject: CS_ACTION_TYPES[0], body: '', due_date: '' });
  const [q, setQ] = useState(''); const [results, setResults] = useState([]); const [store, setStore] = useState(presetStore || null);
  const [bucketF, setBucketF] = useState('');
  const [share, setShare] = useState(false); const [emails, setEmails] = useState(''); const [msg, setMsg] = useState(''); const [busy, setBusy] = useState(false);
  const [editT, setEditT] = useState(null); const [ef, setEf] = useState({ subject: '', body: '', due_date: '' });
  const openEdit = (t) => { setEditT(t); setEf({ subject: t.subject || CS_ACTION_TYPES[0], body: t.body || '', due_date: (t.due_date || '').slice(0, 10) }); };
  const saveEdit = async (extra = {}) => { try { await api().patch(`/capex-sales/activities/${editT.id}`, { subject: ef.subject, body: ef.body || null, due_date: ef.due_date || null, ...extra }); toast.success(extra.done ? 'Action clôturée' : 'Action mise à jour'); setEditT(null); load(); } catch (e) { toast.error('Erreur'); } };
  const delAction = async (t) => { if (!window.confirm('Supprimer définitivement cette action ?')) return; try { await api().delete(`/capex-sales/activities/${t.id}`); toast.success('Action supprimée'); setEditT(null); load(); } catch (e) { toast.error('Erreur'); } };
  const isAdmin = ['super_admin', 'admin'].includes(user?.role) || (user?.email || '').toLowerCase() === 'c.perez@wasabicorner.fr';
  const sendRecap = async () => {
    if (!window.confirm('Envoyer le récapitulatif des actions en retard (+1 mois / URGENCE) à chaque commercial concerné ?')) return;
    try { const r = await api().post('/capex-sales/actions/send-weekly-recap?force=true'); toast.success(`Récap envoyé : ${r.data?.emails_sent || 0} email(s) à ${r.data?.owners || 0} commercial(aux)`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Envoi impossible'); }
  };
  const load = useCallback(() => { api().get('/capex-sales/activities', { params: { only_tasks: 'true' } }).then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, [api]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => { if (q.trim().length >= 2) api().get('/capex-sales/accounts', { params: { q, limit: 8 } }).then((r) => setResults(r.data.items || [])); else setResults([]); }, 250); return () => clearTimeout(t); }, [q, api]);
  const toggle = async (t) => { setItems((xs) => xs.map((x) => x.id === t.id ? { ...x, done: !x.done } : x)); try { await api().patch(`/capex-sales/activities/${t.id}`, { done: !t.done }); } catch (e) { toast.error('Erreur'); load(); } };
  const add = async () => {
    if (!nt.subject) return toast.error("Choisissez une action");
    try {
      await api().post('/capex-sales/activities', {
        type: 'Tâche', subject: nt.subject, body: nt.body || null, due_date: nt.due_date || null, done: false,
        owner_name: user?.name || user?.email || null,
        related_type: store ? 'account' : null, related_id: store ? store.id : null, related_name: store ? store.name : null,
      });
      setNt({ subject: CS_ACTION_TYPES[0], body: '', due_date: '' }); if (!presetStore) setStore(null); setAdding(false); toast.success('Action créée'); load();
    } catch (e) { toast.error('Erreur'); }
  };
  const todo = (items || []).filter((t) => !t.done);
  const done = (items || []).filter((t) => t.done);
  const bucketOf = (t) => actionBucket(overdueDays(t.due_date));
  const counts = { '15': 0, '1m': 0, urgence: 0 };
  todo.forEach((t) => { const b = bucketOf(t); if (b) counts[b] += 1; });
  const shown = bucketF ? todo.filter((t) => bucketOf(t) === bucketF) : todo;
  const dl = async (fmt) => {
    try {
      const res = await api().get('/capex-sales/actions/export', { params: { fmt, bucket: bucketF }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data); const link = document.createElement('a'); link.href = url;
      link.download = `actions_a_mener${bucketF ? '_' + bucketF : ''}.${fmt === 'pdf' ? 'pdf' : 'xlsx'}`; document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export impossible'); }
  };
  const doShare = async () => {
    const rec = emails.split(/[,;\s]+/).map((x) => x.trim()).filter((x) => x.includes('@'));
    if (rec.length === 0) return toast.error('Ajoutez au moins un email valide');
    setBusy(true);
    try { const r = await api().post('/capex-sales/actions/share', { recipients: rec, bucket: bucketF, message: msg || null }); toast.success(r.data?.mocked ? `Préparé pour ${rec.length} destinataire(s) (email non configuré)` : `Envoyé à ${rec.length} destinataire(s)`); setShare(false); setEmails(''); setMsg(''); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Envoi impossible'); } finally { setBusy(false); }
  };
  const Row = (t) => {
    const days = overdueDays(t.due_date); const b = actionBucket(days);
    const tone = b === 'urgence' ? 'alert' : b === '1m' ? 'warn' : b === '15' ? 'warn' : null;
    return (
      <div key={t.id} className="cx-oppitem" style={{ cursor: 'default', alignItems: 'flex-start' }} data-testid={`cs-action-${t.id}`}>
        <button className="cx-stepbtn" title={t.done ? 'Rouvrir' : 'Marquer réalisé'} style={{ width: 28, height: 28, borderRadius: 999, color: t.done ? 'var(--green)' : 'var(--ink-3)', flexShrink: 0, marginTop: 2 }} onClick={(e) => { e.stopPropagation(); toggle(t); }} data-testid={`cs-action-toggle-${t.id}`}>{t.done ? '✓' : ''}</button>
        <div className="flex-1 min-w-0" style={{ cursor: 'pointer' }} onClick={() => openEdit(t)} data-testid={`cs-action-open-${t.id}`}>
          {t.related_name && <div className="font-bold truncate" style={{ color: 'var(--ink)', fontSize: 15 }} data-testid={`cs-action-client-${t.id}`}>{t.related_name}</div>}
          <div className={`font-semibold truncate ${t.done ? 'line-through' : ''}`} style={{ color: t.done ? 'var(--ink-3)' : 'var(--accent)', fontSize: 13 }}>{t.subject}</div>
          {t.body && <div className="text-xs truncate cx-muted">{t.body}</div>}
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <CrOwnerChip name={t.owner_name} />
            <span className="text-xs" style={{ color: 'var(--ink-3)' }}>Créé {fmtFrDate(t.created_at)}</span>
            <span className="text-xs font-semibold" style={{ color: days ? '#eb8c74' : 'var(--ink-2)' }}>Échéance {t.due_date ? fmtFrDate(t.due_date) : '—'}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!t.done && days > 0 && <CxBadge tone={tone || 'alert'}>{days} j de retard</CxBadge>}
          <button className="cx-stepbtn" title="Modifier" style={{ width: 30, height: 30 }} onClick={(e) => { e.stopPropagation(); openEdit(t); }} data-testid={`cs-action-edit-${t.id}`}><Pencil className="w-4 h-4" /></button>
          <button className="cx-stepbtn" title="Supprimer" style={{ width: 30, height: 30, color: '#eb563b' }} onClick={(e) => { e.stopPropagation(); delAction(t); }} data-testid={`cs-action-delete-${t.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
    );
  };
  return (
    <div data-testid="cs-actions">
      {editT && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setEditT(null)}>
          <div className="cx-surface p-4 space-y-3" style={{ maxWidth: 460, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()} data-testid="cs-action-edit">
            <div className="flex items-center justify-between"><p className="cx-h1" style={{ fontSize: 18, margin: 0 }}>Modifier l'action</p><button className="cx-stepbtn" onClick={() => setEditT(null)} data-testid="cs-action-edit-close"><X className="w-4 h-4" /></button></div>
            {editT.related_name && <div className="text-xs cx-muted">Client / prospect : <span style={{ color: 'var(--ink)', fontWeight: 700 }}>{editT.related_name}</span></div>}
            <div><label className="cx-micro">Action</label>
              <select className={selCls} style={selSt} value={ef.subject} onChange={(e) => setEf({ ...ef, subject: e.target.value })} data-testid="cs-action-edit-type">
                {[...new Set([ef.subject, ...CS_ACTION_TYPES])].map((o) => <option key={o}>{o}</option>)}
              </select></div>
            <div><label className="cx-micro">Commentaires</label><textarea className={selCls} style={{ ...selSt, height: 80 }} value={ef.body} onChange={(e) => setEf({ ...ef, body: e.target.value })} data-testid="cs-action-edit-comment" /></div>
            <div><label className="cx-micro">Replanifier l'échéance</label><input type="date" className={selCls} style={selSt} value={ef.due_date} onChange={(e) => setEf({ ...ef, due_date: e.target.value })} data-testid="cs-action-edit-due" /></div>
            <div className="flex gap-2 flex-wrap pt-1">
              <button className="cx-actionbtn primary" style={{ marginTop: 0, flex: 1, minWidth: 130 }} onClick={() => saveEdit()} data-testid="cs-action-edit-save">Enregistrer</button>
              {!editT.done && <button className="cx-actionbtn ghost" style={{ marginTop: 0, flex: 1, minWidth: 130, color: 'var(--green)' }} onClick={() => saveEdit({ done: true })} data-testid="cs-action-edit-done"><Check className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Marquer réalisé</button>}
            </div>
            <button className="cx-actionbtn ghost" style={{ marginTop: 0, width: '100%', color: '#eb563b' }} onClick={() => delAction(editT)} data-testid="cs-action-edit-delete"><Trash2 className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Supprimer l'action</button>
          </div>
        </div>
      )}
      <CxBack label="Retour à l'accueil" onClick={onBack} testid="cs-back" />
      <div className="flex items-center justify-between mb-4"><h1 className="cx-h1">Actions à mener</h1><span className="text-xs cx-muted-3">{todo.length} à traiter</span></div>

      <div className="grid grid-cols-3 gap-2 mb-3" data-testid="cs-action-buckets">
        {CS_BUCKETS.map((bk) => (
          <button key={bk.key} type="button" onClick={() => setBucketF((v) => v === bk.key ? '' : bk.key)} data-testid={`cs-action-bucket-${bk.key}`}
            className="rounded-xl p-3 text-center" style={{ background: 'var(--bg-2)', border: `1px solid ${bucketF === bk.key ? bk.color : 'var(--line)'}`, cursor: 'pointer' }}>
            <div className="cx-num font-bold" style={{ fontSize: 26, color: bk.color, lineHeight: 1 }}>{counts[bk.key]}</div>
            <div className="font-bold mt-1" style={{ fontSize: 12, color: 'var(--ink)' }}>{bk.label}</div>
            <div className="text-[10px] cx-muted-3">{bk.sub}</div>
          </button>
        ))}
      </div>
      <div className="flex gap-2 mb-3">
        <button className="cx-actionbtn ghost" style={{ marginTop: 0, flex: 1 }} onClick={() => dl('excel')} data-testid="cs-action-export-excel"><Upload className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Excel</button>
        <button className="cx-actionbtn ghost" style={{ marginTop: 0, flex: 1 }} onClick={() => dl('pdf')} data-testid="cs-action-export-pdf"><FileText className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />PDF</button>
        <button className="cx-actionbtn ghost" style={{ marginTop: 0, flex: 1 }} onClick={() => setShare(true)} data-testid="cs-action-share"><Mail className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Partager</button>
      </div>
      {isAdmin && <button className="cx-actionbtn ghost" style={{ marginTop: 0, width: '100%', marginBottom: 12 }} onClick={sendRecap} data-testid="cs-action-weekly-recap"><Bell className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Envoyer le récap des retards aux commerciaux</button>}
      {bucketF && <p className="cx-micro mb-2">Filtre : {CS_BUCKETS.find((x) => x.key === bucketF)?.label} · <button style={{ color: 'var(--accent)', fontWeight: 700 }} onClick={() => setBucketF('')} data-testid="cs-action-bucket-clear">✕ retirer</button></p>}

      {share && (
        <div className="cx-surface p-3 mb-3 space-y-2" data-testid="cs-action-share-panel">
          <p className="cx-micro" style={{ fontWeight: 700 }}>PARTAGER PAR MAIL{bucketF ? ` — ${CS_BUCKETS.find((x) => x.key === bucketF)?.label}` : ''}</p>
          <input className={selCls} style={selSt} placeholder="emails séparés par virgule…" value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="cs-action-share-emails" />
          <textarea className={selCls} style={{ ...selSt, height: 60 }} placeholder="Message (optionnel)…" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="cs-action-share-msg" />
          <div className="flex gap-2"><button className="cx-actionbtn primary" style={{ marginTop: 0 }} onClick={doShare} disabled={busy} data-testid="cs-action-share-send">{busy ? 'Envoi…' : 'Envoyer'}</button><button className="cx-actionbtn ghost" style={{ marginTop: 0 }} onClick={() => setShare(false)}>Annuler</button></div>
        </div>
      )}

      {adding ? (
        <div className="cx-surface p-3 mb-4 space-y-2" data-testid="cs-action-form">
          {presetStore ? (
            <div className="cx-oppitem" style={{ cursor: 'default', borderColor: 'var(--accent)' }} data-testid="cs-action-store-locked"><div className="flex-1"><div className="font-semibold" style={{ color: 'var(--ink)' }}>{store?.name}</div><div className="text-xs cx-muted">{[store?.city, store?.cp].filter(Boolean).join(' ')}</div></div><CxBadge tone="client">Rattaché</CxBadge></div>
          ) : (store ? (
            <div className="cx-oppitem" style={{ cursor: 'default' }}><div className="flex-1"><div className="font-semibold" style={{ color: 'var(--ink)' }}>{store.name}</div><div className="text-xs cx-muted">{[store.city, store.cp].filter(Boolean).join(' ')}</div></div><button className="cx-stepbtn" onClick={() => setStore(null)}>×</button></div>
          ) : (<>
            <CxSearch value={q} onChange={setQ} placeholder="Client / prospect concerné (optionnel)…" testid="cs-action-store-search" />
            <div className="space-y-2">{results.map((a) => <CxItem key={a.id} avatar={initials(a.name)} title={a.name} subtitle={[a.city, a.type].filter(Boolean).join(' · ')} onClick={() => { setStore(a); setResults([]); setQ(''); }} testid={`cs-action-store-${a.id}`} />)}</div>
          </>))}
          <div><label className="cx-micro">Action</label>
            <select className={selCls} style={selSt} value={nt.subject} onChange={(e) => setNt({ ...nt, subject: e.target.value })} data-testid="cs-action-type">
              {CS_ACTION_TYPES.map((o) => <option key={o}>{o}</option>)}
            </select></div>
          <div><label className="cx-micro">Commentaires</label><textarea className={selCls} style={{ ...selSt, height: 64 }} placeholder="Texte libre…" value={nt.body} onChange={(e) => setNt({ ...nt, body: e.target.value })} data-testid="cs-action-comment" /></div>
          <div><label className="cx-micro">Date d'échéance du traitement</label><input type="date" className={selCls} style={selSt} value={nt.due_date} onChange={(e) => setNt({ ...nt, due_date: e.target.value })} data-testid="cs-action-due" /></div>
          <div className="flex gap-2"><button className="cx-actionbtn primary" style={{ marginTop: 0 }} onClick={add} data-testid="cs-action-save">Créer l'action</button><button className="cx-actionbtn ghost" style={{ marginTop: 0 }} onClick={() => setAdding(false)}>Annuler</button></div>
        </div>
      ) : <button className="cx-actionbtn primary" onClick={() => setAdding(true)} data-testid="cs-action-new"><Plus className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Nouvelle action</button>}
      {items === null ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div> : (
        <>
          <p className="cx-micro mt-5 mb-2">À traiter ({shown.length}{bucketF ? ` / ${todo.length}` : ''})</p>
          {shown.length === 0 ? <CxEmpty>{bucketF ? 'Aucune action dans cette tranche.' : 'Aucune action en attente. 🎯'}</CxEmpty> : <div className="space-y-2" style={{ maxHeight: '60vh', overflowY: 'auto' }}>{shown.map(Row)}</div>}
          {!bucketF && done.length > 0 && <><p className="cx-micro mt-5 mb-2">Terminées ({done.length})</p><div className="space-y-2">{done.map(Row)}</div></>}
        </>
      )}
    </div>
  );
}


function Photos({ api, token, onBack }) {
  const [q, setQ] = useState(''); const [results, setResults] = useState([]); const [store, setStore] = useState(null);
  const [docs, setDocs] = useState([]); const [loading, setLoading] = useState(false); const [uploading, setUploading] = useState(false);
  useEffect(() => { const t = setTimeout(() => { if (q.trim().length >= 2) api().get('/capex-sales/accounts', { params: { q, limit: 8 } }).then((r) => setResults(r.data.items || [])); else setResults([]); }, 250); return () => clearTimeout(t); }, [q, api]);
  const loadDocs = useCallback((id) => { setLoading(true); api().get('/capex-sales/documents', { params: { related_type: 'account', related_id: id } }).then((r) => setDocs((r.data.items || []).filter((d) => (d.content_type || '').startsWith('image/')))).catch(() => setDocs([])).finally(() => setLoading(false)); }, [api]);
  const pick = (a) => { setStore(a); setResults([]); setQ(''); loadDocs(a.id); };
  const onUpload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ''; if (!file || !store) return;
    if (!file.type.startsWith('image/')) return toast.error('Choisissez une image');
    const fd = new FormData(); fd.append('file', file); setUploading(true);
    try { await api().post('/capex-sales/documents/upload', fd, { params: { related_type: 'account', related_id: store.id, related_name: store.name } }); toast.success('Photo ajoutée'); loadDocs(store.id); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); } finally { setUploading(false); }
  };
  const url = (d) => `${BACKEND_URL}/api/capex-sales/documents/${d.id}/download?auth=${token}`;
  return (
    <div data-testid="cs-photos">
      <CxBack label="Retour à l'accueil" onClick={onBack} testid="cs-back" />
      <div className="flex items-center justify-between mb-4"><h1 className="cx-h1">Photos</h1><span className="text-xs cx-muted-3">Terrain</span></div>
      {store ? (
        <>
          <div className="cx-oppitem mb-4" style={{ cursor: 'default' }}><Camera className="w-5 h-5" style={{ color: 'var(--green)' }} /><div className="flex-1"><div className="font-semibold" style={{ color: 'var(--ink)' }}>{store.name}</div><div className="text-xs cx-muted">{[store.city, store.cp].filter(Boolean).join(' ')}</div></div><button className="cx-stepbtn" onClick={() => { setStore(null); setDocs([]); }}>×</button></div>
          <label className="cx-actionbtn primary" style={{ display: 'block', textAlign: 'center', cursor: 'pointer' }} data-testid="cs-photo-upload">
            {uploading ? 'Envoi…' : <><Upload className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Ajouter une photo</>}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onUpload} disabled={uploading} data-testid="cs-photo-input" />
          </label>
          <p className="cx-micro mt-5 mb-2">Galerie ({docs.length})</p>
          {loading ? <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>
            : docs.length === 0 ? <CxEmpty>Aucune photo pour ce magasin.</CxEmpty>
              : <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{docs.map((d) => (
                <a key={d.id} href={url(d)} target="_blank" rel="noreferrer" className="block rounded-xl overflow-hidden" style={{ border: '1px solid var(--line)' }} data-testid={`cs-photo-${d.id}`}>
                  <img src={url(d)} alt={d.original_filename} style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }} />
                  <div className="text-xs truncate px-2 py-1.5 cx-muted" style={{ background: 'var(--bg-2)' }}>{d.original_filename}</div>
                </a>
              ))}</div>}
        </>
      ) : (<>
        <CxSearch value={q} onChange={setQ} placeholder="Choisir un magasin pour ses photos…" testid="cs-photos-search" />
        <div className="space-y-2 mt-2">{results.map((a) => <CxItem key={a.id} avatar={initials(a.name)} title={a.name} subtitle={[a.city, a.type].filter(Boolean).join(' · ')} onClick={() => pick(a)} testid={`cs-photos-pick-${a.id}`} />)}</div>
        {q.trim().length < 2 && <p className="cx-empty">Recherchez un magasin pour voir et ajouter des photos terrain.</p>}
      </>)}
    </div>
  );
}

const ETUDE_STATUSES = [
  { key: 'all', label: 'Toutes' },
  { key: 'viable', label: 'Viable' },
  { key: 'risky', label: 'À risque' },
  { key: 'non-viable', label: 'Non viable' },
];

function EtudesCapex({ api, onBack }) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  useEffect(() => { api().get('/simulations', { params: { mine: true } }).then((r) => setItems(r.data || [])).catch(() => setItems([])); }, [api]);
  const badge = (s) => s === 'viable' ? 'client' : s === 'risky' ? 'warn' : s === 'non-viable' ? 'alert' : 'neutral';
  const badgeLbl = (s) => s === 'viable' ? 'Viable' : s === 'risky' ? 'À risque' : s === 'non-viable' ? 'Non viable' : (s || 'à traiter');
  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('fr-FR'); } catch { return ''; } };

  const filtered = useMemo(() => {
    if (!items) return [];
    const query = q.trim().toLowerCase();
    return items.filter((s) => {
      if (statusFilter !== 'all' && s.status !== statusFilter) return false;
      if (!query) return true;
      return [s.retailer_name, s.project_name].filter(Boolean).join(' ').toLowerCase().includes(query);
    });
  }, [items, q, statusFilter]);

  const counts = useMemo(() => {
    const c = { all: items ? items.length : 0, viable: 0, risky: 0, 'non-viable': 0 };
    (items || []).forEach((s) => { if (c[s.status] !== undefined) c[s.status] += 1; });
    return c;
  }, [items]);

  if (selected) return <EtudeDetail item={selected} onBack={() => setSelected(null)} />;

  return (
    <div data-testid="cs-etudes">
      <CxBack label="Retour à l'accueil" onClick={onBack} testid="cs-back" />
      <div className="flex items-center justify-between mb-4"><h1 className="cx-h1">Mes études CAPEX</h1><span className="text-xs cx-muted-3">{items ? items.length : ''} étude(s)</span></div>
      <p className="text-xs cx-muted mb-3">Uniquement les études CAPEX que vous avez créées.</p>
      <div className="mb-3"><CxSearch value={q} onChange={setQ} placeholder="Rechercher une étude (enseigne, projet)…" testid="cs-etudes-search" /></div>
      <div className="flex flex-wrap gap-2 mb-4">
        {ETUDE_STATUSES.map((st) => (
          <span key={st.key} onClick={() => setStatusFilter(st.key)} className="cx-chip" data-testid={`cs-etudes-filter-${st.key}`}
            style={{ cursor: 'pointer', background: statusFilter === st.key ? 'var(--accent)' : 'var(--tile)', color: statusFilter === st.key ? 'var(--on-accent)' : 'var(--ink)', borderColor: 'var(--line-2)' }}>
            {st.label} ({counts[st.key] ?? 0})
          </span>
        ))}
      </div>
      {items === null ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>
        : items.length === 0 ? <CxEmpty>Vous n'avez créé aucune étude CAPEX pour le moment.</CxEmpty>
          : filtered.length === 0 ? <CxEmpty>Aucune étude ne correspond à votre recherche.</CxEmpty>
            : <div className="space-y-2">{filtered.map((s) => {
              const title = [s.retailer_name, s.project_name].filter(Boolean).join(' — ') || 'Étude CAPEX';
              return (
                <button key={s.id} onClick={() => setSelected(s)} className="cx-oppitem w-full text-left" data-testid={`cs-etude-${s.id}`}>
                  <Calculator className="w-5 h-5" style={{ color: '#e0b45a' }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate" style={{ color: 'var(--ink)' }}>{title}</div>
                    <div className="text-xs cx-muted truncate">CAPEX {fmtK(s.total_capex)} · retour {s.payback_months ? Math.round(s.payback_months) + ' mois' : '—'} · CA {fmtK(s.ca_potential_ht)} · {fmtDate(s.created_at)}</div>
                  </div>
                  <CxBadge tone={badge(s.status)}>{badgeLbl(s.status)}</CxBadge>
                </button>
              );
            })}</div>}
    </div>
  );
}

function EtudeDetail({ item, onBack }) {
  const navigate = useNavigate();
  const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString('fr-FR') : '—'; } catch { return '—'; } };
  const statusLbl = item.status === 'viable' ? 'Viable' : item.status === 'risky' ? 'À risque' : item.status === 'non-viable' ? 'Non viable' : (item.status || 'à traiter');
  const statusTone = item.status === 'viable' ? 'client' : item.status === 'risky' ? 'warn' : item.status === 'non-viable' ? 'alert' : 'neutral';
  const adminLbl = { approved: 'GO ✓', rejected: 'NO GO', pending: "À l'étude", pending_review: "À l'étude", under_review: 'En revue' }[item.admin_status] || (item.admin_status ? item.admin_status : "À l'étude");
  const bizLbl = ['signed', 'won'].includes(item.business_status) ? 'Signé' : item.business_status === 'lost' ? 'Perdu' : 'En cours';
  const pct = (v) => (v || v === 0) ? Math.round(v * (v <= 1 ? 100 : 1)) + ' %' : '—';
  const rows = [
    ['CAPEX total', fmtK(item.total_capex)],
    ['Porteur CAPEX', item.capex_carrier || '—'],
    ['CA prévisionnel HT', fmtK(item.ca_potential_ht)],
    ['Retour sur investissement', item.payback_months ? Math.round(item.payback_months) + ' mois' : '—'],
    ['Hypothèse recommandée', item.recommended_hypothesis || '—'],
    ['Taux de commission', pct(item.commission_rate_used)],
    ['Mois rouges', (item.red_months_count ?? item.system_red_months_count ?? '—') === '—' ? '—' : String(item.red_months_count ?? item.system_red_months_count)],
    ["Date d'ouverture souhaitée", fmtDate(item.opening_date)],
    ['Date de création', fmtDate(item.created_at)],
    ['Créée par', item.creator_name || '—'],
  ];
  return (
    <div data-testid="cs-etude-detail">
      <CxBack label="Retour aux études" onClick={onBack} testid="cs-etude-detail-back" />
      <div className="flex items-center gap-3 mb-4">
        <span className="cx-tile__icon" style={{ width: 48, height: 48, borderRadius: 14 }}><Calculator className="w-6 h-6" style={{ color: '#e0b45a' }} /></span>
        <div className="min-w-0">
          <h1 className="cx-h1 truncate">{[item.retailer_name, item.project_name].filter(Boolean).join(' — ') || 'Étude CAPEX'}</h1>
          <div className="text-xs cx-muted">Étude CAPEX personnelle</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <CxBadge tone={statusTone}>{statusLbl}</CxBadge>
        <CxBadge tone={item.admin_status === 'approved' ? 'client' : item.admin_status === 'rejected' ? 'alert' : 'neutral'}>Direction : {adminLbl}</CxBadge>
        <CxBadge tone={bizLbl === 'Signé' ? 'client' : bizLbl === 'Perdu' ? 'alert' : 'warn'}>{bizLbl}</CxBadge>
      </div>
      <CxAccordion title="Synthèse de l'étude" defaultOpen testid="cs-etude-detail-summary">
        <Grid rows={rows} />
      </CxAccordion>
      {item.commercial_comment && (
        <CxAccordion title="Commentaire commercial" defaultOpen testid="cs-etude-detail-comment">
          <p className="text-sm" style={{ color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>{item.commercial_comment}</p>
        </CxAccordion>
      )}
      <button className="cx-actionbtn primary mt-4" onClick={() => navigate(`/simulations/${item.id}`)} data-testid="cs-etude-detail-open-full">Ouvrir l'étude complète</button>
    </div>
  );
}

function ContactCard({ c, showAccount = true, testid }) {
  const [open, setOpen] = useState(false);
  const cf = c.custom_fields || {};
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || '—';
  const tel = c.mobile || c.phone;
  const btn = { marginTop: 0, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 };
  const detail = [
    ['Civilité', cf.civilite], ['Poste', c.role_title], ['Poste 2', cf.poste_2],
    ['Email', c.email], ['Email 2', cf.email_2],
    ['Téléphone', c.phone], ['Mobile', c.mobile], ['Portable privé', cf.portable_prive],
    ['Responsabilité groupement', cf.responsabilite_groupement], ['Qualification SONCAS', cf.qualification_soncas],
    ['Client VIP', cf.client_vip ? 'Oui ✓' : 'Non'],
  ];
  if (showAccount) detail.push(['Compte', c.account_name]);
  return (
    <div className="p-3 cx-surface" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="cx-pct" style={{ color: 'var(--accent)', borderColor: 'var(--line-2)', fontSize: 13 }}>{initials(name)}</span>
        <button className="flex-1 min-w-0 text-left" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`}>
          <div className="font-semibold truncate flex items-center gap-1.5" style={{ color: 'var(--ink)' }}>
            {cf.civilite ? cf.civilite + ' ' : ''}{name}{cf.client_vip && <Trophy className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />}
          </div>
          <div className="text-xs cx-muted truncate">{[c.role_title, showAccount ? c.account_name : null].filter(Boolean).join(' · ') || '—'}</div>
        </button>
        <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: 'var(--ink-3)' }} />
      </div>
      <div className="flex gap-2 mt-2">
        {tel && <a href={`tel:${tel}`} className="cx-actionbtn ghost" style={btn} data-testid={`${testid}-call`}><Phone className="w-4 h-4" /> Appeler</a>}
        {c.email && <a href={`mailto:${c.email}`} className="cx-actionbtn ghost" style={btn} data-testid={`${testid}-mail`}><Mail className="w-4 h-4" /> Email</a>}
      </div>
      {open && <div className="mt-3" style={{ borderTop: '1px solid var(--line)', paddingTop: 10 }}><Grid rows={detail.filter(([, v]) => v !== undefined && v !== null && v !== '')} /></div>}
    </div>
  );
}

function ContactAnnuaire({ api, onBack }) {
  const [q, setQ] = useState(''); const [items, setItems] = useState([]); const [loading, setLoading] = useState(true);
  const load = useCallback((query) => { setLoading(true); api().get('/capex-sales/contacts', { params: { q: query || '' } }).then((r) => setItems(r.data.items || [])).catch(() => setItems([])).finally(() => setLoading(false)); }, [api]);
  useEffect(() => { const t = setTimeout(() => load(q), 250); return () => clearTimeout(t); }, [q, load]);
  return (
    <div data-testid="cs-contact">
      <CxBack label="Retour à l'accueil" onClick={onBack} testid="cs-back" />
      <div className="flex items-center justify-between mb-4"><h1 className="cx-h1">Contact</h1><span className="text-xs cx-muted-3">Annuaire</span></div>
      <div className="mb-4"><CxSearch value={q} onChange={setQ} placeholder="Rechercher un contact (nom, rôle, email)…" testid="cs-contact-search" /></div>
      {loading ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>
        : items.length === 0 ? <CxEmpty>Aucun contact.</CxEmpty>
          : <div className="space-y-2">{items.map((c) => <ContactCard key={c.id} c={c} testid={`cs-contact-${c.id}`} />)}</div>}
    </div>
  );
}

function Parametres({ user, onBack, onNav }) {
  const [prefs, setPrefs] = useState(() => { try { return JSON.parse(localStorage.getItem('cs_prefs') || '{}'); } catch { return {}; } });
  const set = (k, v) => { const next = { ...prefs, [k]: v }; setPrefs(next); localStorage.setItem('cs_prefs', JSON.stringify(next)); };
  const Toggle = ({ k, label, desc }) => (
    <div className="flex items-center justify-between p-3 cx-surface" data-testid={`cs-pref-${k}`}>
      <div className="flex-1 pr-3"><div className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{label}</div>{desc && <div className="text-xs cx-muted">{desc}</div>}</div>
      <button onClick={() => set(k, !prefs[k])} data-testid={`cs-pref-toggle-${k}`} style={{ width: 46, height: 26, borderRadius: 999, background: prefs[k] ? 'var(--green)' : 'var(--line-2)', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
        <span style={{ position: 'absolute', top: 3, left: prefs[k] ? 23 : 3, width: 20, height: 20, borderRadius: 999, background: '#fff', transition: 'left .2s' }} />
      </button>
    </div>
  );
  return (
    <div data-testid="cs-parametres">
      <CxBack label="Retour à l'accueil" onClick={onBack} testid="cs-back" />
      <h1 className="cx-h1 mb-4">Paramètres</h1>
      <div className="flex items-center gap-3 p-3 cx-surface mb-4">
        <span className="cx-pct" style={{ color: 'var(--accent)', borderColor: 'var(--line-2)', fontSize: 14 }}>{initials(user?.name || user?.email || 'U')}</span>
        <div><div className="font-semibold" style={{ color: 'var(--ink)' }}>{user?.name || user?.email}</div><div className="text-xs cx-muted">{user?.role}</div></div>
      </div>
      <p className="cx-micro mb-2">Préférences</p>
      <div className="space-y-2">
        <Toggle k="notifications" label="Notifications" desc="Alertes relances & échéances de contrat" />
        <Toggle k="offline" label="Synchronisation hors-ligne" desc="Comptes rendus & photos disponibles sans réseau" />
        <Toggle k="darklaunch" label="Ouvrir sur l'accueil" desc="Afficher le lanceur au démarrage" />
      </div>
      <p className="cx-micro mt-5 mb-2">Données &amp; exports</p>
      <div className="space-y-2">
        {['super_admin', 'admin'].includes(user?.role) && (
          <button onClick={() => onNav('import')} className="flex items-center justify-between p-3 cx-surface w-full" style={{ textAlign: 'left' }} data-testid="cs-params-import">
            <div><div className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>Import &amp; sauvegarde des données</div><div className="text-xs cx-muted">Mettre à jour les comptes depuis un fichier Excel (mapping)</div></div>
            <Upload className="w-5 h-5" style={{ color: 'var(--accent)' }} />
          </button>
        )}
        {['super_admin', 'admin'].includes(user?.role) && (
          <button onClick={() => onNav('doublons')} className="flex items-center justify-between p-3 cx-surface w-full" style={{ textAlign: 'left' }} data-testid="cs-params-doublons">
            <div><div className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>Nettoyage des doublons</div><div className="text-xs cx-muted">Comptes en double (même enseigne + ville)</div></div>
            <Users className="w-5 h-5" style={{ color: '#e0b45a' }} />
          </button>
        )}
        {['super_admin', 'admin'].includes(user?.role) && (
          <button onClick={() => onNav('rubriques')} className="flex items-center justify-between p-3 cx-surface w-full" style={{ textAlign: 'left' }} data-testid="cs-params-rubriques">
            <div><div className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>Ordre des rubriques (fiche)</div><div className="text-xs cx-muted">Réorganiser les blocs de la fiche, appliqué à tous</div></div>
            <Settings2 className="w-5 h-5" style={{ color: '#5b9bd5' }} />
          </button>
        )}
        {['super_admin', 'admin'].includes(user?.role) && (
          <button onClick={() => onNav('cr-listes')} className="flex items-center justify-between p-3 cx-surface w-full" style={{ textAlign: 'left' }} data-testid="cs-params-cr-listes">
            <div><div className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>Listes déroulantes (Comptes rendus)</div><div className="text-xs cx-muted">Contact rencontré, Origine, Objet, Résultat — créer, supprimer, glisser</div></div>
            <ClipboardList className="w-5 h-5" style={{ color: '#7a6fe0' }} />
          </button>
        )}
        {['super_admin', 'admin'].includes(user?.role) && (
          <button onClick={() => onNav('audit')} className="flex items-center justify-between p-3 cx-surface w-full" style={{ textAlign: 'left' }} data-testid="cs-params-audit">
            <div><div className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>Journal d'audit</div><div className="text-xs cx-muted">Créations &amp; suppressions (qui, quoi, quand)</div></div>
            <ShieldCheck className="w-5 h-5" style={{ color: 'var(--green)' }} />
          </button>
        )}
        <div className="flex items-center justify-between p-3 cx-surface"><div><div className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>Zone géographique</div><div className="text-xs cx-muted">Périmètre de visualisation</div></div><CxBadge tone="neutral">National</CxBadge></div>
        <div className="flex items-center justify-between p-3 cx-surface"><div><div className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>Export Power BI</div><div className="text-xs cx-muted">Flux de données analytique</div></div><CxBadge tone="client">OK</CxBadge></div>
        <div className="flex items-center justify-between p-3 cx-surface"><div><div className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>Gestion de l'équipe</div><div className="text-xs cx-muted">Rôles &amp; droits (selon habilitation)</div></div><span className="cx-muted">›</span></div>
      </div>
    </div>
  );
}

function CRDropdownsAdmin({ api, onBack }) {
  const [fields, setFields] = useState([]);
  const [lists, setLists] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newVals, setNewVals] = useState({});
  const dragRef = useRef(null);
  const load = useCallback(() => {
    setLoading(true);
    api().get('/capex-sales/cr-dropdowns')
      .then((r) => { setFields(r.data.fields || []); setLists(r.data.lists || {}); })
      .catch(() => toast.error('Chargement impossible'))
      .finally(() => setLoading(false));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const setField = (k, arr) => setLists((s) => ({ ...s, [k]: arr }));
  const addItem = (k) => {
    const v = (newVals[k] || '').trim();
    if (!v) return;
    if ((lists[k] || []).some((x) => x.toLowerCase() === v.toLowerCase())) { toast.error('Valeur déjà présente'); return; }
    setField(k, [...(lists[k] || []), v]);
    setNewVals((s) => ({ ...s, [k]: '' }));
  };
  const delItem = (k, i) => setField(k, (lists[k] || []).filter((_, idx) => idx !== i));
  const editItem = (k, i, v) => setField(k, (lists[k] || []).map((x, idx) => (idx === i ? v : x)));
  const move = (k, i, dir) => {
    const arr = [...(lists[k] || [])]; const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]]; setField(k, arr);
  };
  const onDrop = (k, i) => {
    const d = dragRef.current;
    if (!d || d.k !== k || d.i === i) return;
    const arr = [...(lists[k] || [])]; const [it] = arr.splice(d.i, 1); arr.splice(i, 0, it);
    setField(k, arr); dragRef.current = null;
  };
  const save = async () => {
    setSaving(true);
    try { await api().put('/capex-sales/cr-dropdowns', { lists }); toast.success('Listes enregistrées ✓'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="py-24 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>;
  return (
    <div data-testid="cs-cr-listes">
      <CxBack label="Retour aux paramètres" onClick={onBack} testid="cs-cr-listes-back" />
      <div className="flex items-center justify-between mb-1">
        <h1 className="cx-h1">Listes déroulantes — Comptes rendus</h1>
        <button className="cx-actionbtn primary" onClick={save} disabled={saving} data-testid="cs-cr-listes-save">{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
      </div>
      <p className="cx-micro mb-4">Ajoutez, renommez, supprimez ou glissez-déposez les valeurs. Elles alimentent le formulaire de compte rendu.</p>
      <div className="space-y-5">
        {fields.map((f) => (
          <div key={f.key} className="cx-surface p-3" data-testid={`cs-cr-list-${f.key}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold" style={{ color: 'var(--ink)' }}>{f.label}</div>
              <span className="text-xs cx-muted-3">{(lists[f.key] || []).length} valeur(s)</span>
            </div>
            <div className="space-y-1.5">
              {(lists[f.key] || []).map((v, i) => (
                <div key={i} draggable onDragStart={() => { dragRef.current = { k: f.key, i }; }}
                  onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(f.key, i)}
                  className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'var(--tile)', border: '1px solid var(--line-2)' }}
                  data-testid={`cs-cr-item-${f.key}-${i}`}>
                  <span style={{ cursor: 'grab', color: 'var(--ink-3)', fontSize: 16, lineHeight: 1 }} title="Glisser pour réordonner">⠿</span>
                  <input value={v} onChange={(e) => editItem(f.key, i, e.target.value)} className="flex-1 px-2 py-1 rounded-md text-sm"
                    style={{ background: 'var(--card, #fff)', border: '1px solid var(--line-2)', color: 'var(--ink)' }} data-testid={`cs-cr-input-${f.key}-${i}`} />
                  <button className="cx-stepbtn" onClick={() => move(f.key, i, -1)} title="Monter">↑</button>
                  <button className="cx-stepbtn" onClick={() => move(f.key, i, 1)} title="Descendre">↓</button>
                  <button className="cx-stepbtn" onClick={() => delItem(f.key, i)} title="Supprimer" style={{ color: '#e0563b' }} data-testid={`cs-cr-del-${f.key}-${i}`}>×</button>
                </div>
              ))}
              {(lists[f.key] || []).length === 0 && <div className="text-xs cx-muted-3 py-1">Aucune valeur.</div>}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input value={newVals[f.key] || ''} onChange={(e) => setNewVals((s) => ({ ...s, [f.key]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(f.key); } }}
                placeholder="Ajouter une valeur…" className={selCls} style={{ ...selSt, flex: 1, height: 36, marginTop: 0 }} data-testid={`cs-cr-add-input-${f.key}`} />
              <button className="cx-actionbtn ghost" onClick={() => addItem(f.key)} data-testid={`cs-cr-add-${f.key}`}><Plus className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Ajouter</button>
            </div>
          </div>
        ))}
      </div>
      <button className="cx-actionbtn primary mt-5" onClick={save} disabled={saving} data-testid="cs-cr-listes-save-bottom">{saving ? 'Enregistrement…' : 'Enregistrer les listes'}</button>
    </div>
  );
}


function ImportAdmin({ api, onBack }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [mode, setMode] = useState('upsert');
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState({ mapping: {}, mode: 'upsert' });
  const [history, setHistory] = useState([]);
  const loadMeta = useCallback(() => {
    api().get('/capex-sales/import-mapping').then((r) => setSaved(r.data)).catch(() => {});
    api().get('/capex-sales/import-log').then((r) => setHistory(r.data.items || [])).catch(() => {});
  }, [api]);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ''; if (!f) return;
    setFile(f); setLoading(true); setPreview(null); setResult(null);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api().post('/capex-sales/accounts/import-preview', fd);
      setPreview(r.data);
      const merged = { ...(r.data.suggested || {}) };
      Object.entries(saved.mapping || {}).forEach(([col, tgt]) => { if (r.data.columns.includes(col)) merged[col] = tgt; });
      setMapping(merged);
      if (saved.mode) setMode(saved.mode);
    } catch (err) { toast.error("Impossible de lire le fichier"); } finally { setLoading(false); }
  };
  const saveMapping = async () => {
    try { await api().put('/capex-sales/import-mapping', { mapping, mode }); toast.success('Mapping enregistré — pré-rempli au prochain import'); loadMeta(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
  };
  const apply = async () => {
    if (!Object.values(mapping).includes('std:name')) return toast.error('Mappez la colonne « Nom du compte »');
    setRunning(true);
    try { const fd = new FormData(); fd.append('file', file); const r = await api().post('/capex-sales/accounts/import-apply', fd, { params: { mapping: JSON.stringify(mapping), mode } }); setResult(r.data); toast.success(`Import terminé : ${r.data.created} créé(s), ${r.data.updated} mis à jour`); loadMeta(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Erreur d'import"); } finally { setRunning(false); }
  };
  const groups = (preview?.targets || []).reduce((a, t) => { (a[t.group] = a[t.group] || []).push(t); return a; }, {});

  return (
    <div data-testid="cs-import-admin">
      <CxBack label="Retour aux paramètres" onClick={onBack} testid="cs-import-back" />
      <h1 className="cx-h1 mb-1">Import &amp; sauvegarde</h1>
      <p className="text-xs cx-muted mb-4">Importez un fichier Excel pour créer ou mettre à jour les comptes. Enseigne = 1er mot du nom si vide (Intermarché, Auchan…). Fusion par Nom + Code postal.</p>
      <label className="cx-actionbtn primary" style={{ display: 'block', textAlign: 'center', cursor: 'pointer' }} data-testid="cs-import-file-label">
        {loading ? 'Lecture…' : <><Upload className="w-4 h-4 inline-block mr-1" style={{ verticalAlign: '-3px' }} />Choisir un fichier Excel (.xlsx)</>}
        <input type="file" accept=".xlsx,.xls" className="hidden" onChange={onFile} data-testid="cs-import-file-input" />
      </label>
      {file && <p className="text-xs cx-muted mt-2">{file.name}</p>}

      {result && <div className="cx-surface p-3 mt-4" data-testid="cs-import-result"><div className="font-semibold" style={{ color: 'var(--green)' }}>Import terminé</div><div className="text-sm cx-muted">{result.created} créé(s) · {result.updated} mis à jour · {result.skipped} ignoré(s) · {result.total} ligne(s)</div></div>}

      {preview && !result && (
        <>
          <p className="cx-micro mt-5 mb-2">{preview.total} ligne(s) — associez chaque colonne</p>
          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--line)', maxHeight: 360, overflowY: 'auto' }}>
            {preview.columns.map((col) => (
              <div key={col} className="flex items-center gap-3 px-3 py-2" style={{ borderBottom: '1px solid var(--line)' }} data-testid={`cs-import-col-${col}`}>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate" style={{ color: 'var(--ink)' }}>{col}</div><div className="text-[11px] truncate cx-muted">ex : {(preview.samples[0] || {})[col] || '—'}</div></div>
                <select className="px-2 py-1.5 rounded-lg text-sm" style={{ background: 'var(--tile)', border: '1px solid var(--line-2)', color: 'var(--ink)', maxWidth: 220 }} value={mapping[col] || ''} onChange={(e) => setMapping({ ...mapping, [col]: e.target.value })} data-testid={`cs-import-map-${col}`}>
                  <option value="">— Ignorer —</option>
                  {Object.entries(groups).map(([g, opts]) => <optgroup key={g} label={g}>{opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</optgroup>)}
                </select>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <span className="cx-micro">Mode :</span>
            {[['upsert', 'Créer + mettre à jour'], ['update', 'Mettre à jour seulement'], ['create', 'Créer seulement']].map(([v, l]) => (
              <label key={v} className="flex items-center gap-1.5 text-sm" style={{ color: 'var(--ink)' }}><input type="radio" checked={mode === v} onChange={() => setMode(v)} data-testid={`cs-import-mode-${v}`} /> {l}</label>
            ))}
          </div>
          <button className="cx-actionbtn primary" onClick={apply} disabled={running} data-testid="cs-import-apply">{running ? 'Import en cours…' : "Lancer l'import"}</button>
          <button className="cx-actionbtn ghost" onClick={saveMapping} data-testid="cs-import-savemap">Enregistrer ce mapping (pré-rempli au prochain import)</button>
        </>
      )}

      {history.length > 0 && (
        <>
          <p className="cx-micro mt-6 mb-2">Historique des imports</p>
          <div className="space-y-2">{history.map((h) => (
            <div key={h.id} className="p-3 cx-surface" data-testid={`cs-import-hist-${h.id}`}>
              <div className="flex items-center justify-between"><div className="font-semibold text-sm truncate" style={{ color: 'var(--ink)' }}>{h.filename || 'Import'}</div><span className="text-xs cx-muted-3">{(h.at || '').slice(0, 16).replace('T', ' ')}</span></div>
              <div className="text-xs cx-muted mt-0.5">{h.user_name} · {h.mode} · <span style={{ color: 'var(--green)' }}>{h.created} créé(s)</span> · {h.updated} maj · {h.skipped} ignoré(s)</div>
            </div>
          ))}</div>
        </>
      )}
    </div>
  );
}

function Doublons({ api, onBack }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api().get('/capex-sales/duplicates').then((r) => setData(r.data)).catch(() => setData({ groups: [] })); }, [api]);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { if (!window.confirm('Supprimer ce compte en double ?')) return; try { await api().delete(`/capex-sales/accounts/${id}`); toast.success('Compte supprimé'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Suppression réservée Super Admin'); } };
  return (
    <div data-testid="cs-doublons">
      <CxBack label="Retour aux paramètres" onClick={onBack} testid="cs-doublons-back" />
      <div className="flex items-center justify-between mb-4"><h1 className="cx-h1">Doublons</h1><span className="text-xs cx-muted-3">{data ? data.total_groups : ''} groupe(s)</span></div>
      <p className="text-xs cx-muted mb-4">Comptes partageant la même enseigne et la même ville.</p>
      {data === null ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>
        : data.groups.length === 0 ? <CxEmpty>Aucun doublon détecté. 🎉</CxEmpty>
          : <div className="space-y-3">{data.groups.map((g, i) => (
            <div key={i} className="cx-surface p-3" data-testid={`cs-dup-group-${i}`}>
              <div className="flex items-center gap-2 mb-2"><CxBadge tone="warn">{g.count}</CxBadge><span className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{g.enseigne} · {g.city}</span></div>
              <div className="space-y-1.5">{g.accounts.map((a) => (
                <div key={a.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg" style={{ background: 'var(--tile)' }}>
                  <div className="flex-1 min-w-0"><div className="text-sm truncate" style={{ color: 'var(--ink)' }}>{a.name}</div><div className="text-[11px] cx-muted">{a.cp} · {a.type}</div></div>
                  <button className="cx-stepbtn" style={{ color: 'var(--alert)' }} onClick={() => del(a.id)} data-testid={`cs-dup-del-${a.id}`}>×</button>
                </div>
              ))}</div>
            </div>
          ))}</div>}
    </div>
  );
}

function AuditLog({ api, onBack }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api().get('/capex-sales/audit-log').then((r) => setItems(r.data.items || [])).catch(() => setItems([])); }, [api]);
  const color = (a) => a === 'delete' ? 'var(--alert)' : a === 'create' ? 'var(--green)' : 'var(--info)';
  const lbl = { delete: 'Suppression', create: 'Création', update: 'Modification' };
  return (
    <div data-testid="cs-audit">
      <CxBack label="Retour aux paramètres" onClick={onBack} testid="cs-audit-back" />
      <div className="flex items-center justify-between mb-4"><h1 className="cx-h1">Journal d'audit</h1><span className="text-xs cx-muted-3">{items ? items.length : ''} événement(s)</span></div>
      {items === null ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div>
        : items.length === 0 ? <CxEmpty>Aucun événement enregistré.</CxEmpty>
          : <div className="space-y-2">{items.map((e) => (
            <div key={e.id} className="flex items-center gap-3 p-3 cx-surface" data-testid={`cs-audit-${e.id}`}>
              <span className="cx-pct" style={{ color: color(e.action), borderColor: color(e.action), fontSize: 11, width: 38, height: 38 }}>{(lbl[e.action] || e.action).slice(0, 3)}</span>
              <div className="flex-1 min-w-0"><div className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>{lbl[e.action] || e.action} · {e.entity}{e.label ? ' — ' + e.label : ''}</div><div className="text-xs cx-muted">{e.user_name} ({e.role}) · {(e.at || '').slice(0, 16).replace('T', ' ')}</div></div>
            </div>
          ))}</div>}
    </div>
  );
}


function OrdreRubriques({ api, user, onBack }) {
  const [order, setOrder] = useState(null);
  const [fields, setFields] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const [saving, setSaving] = useState(false);
  const canRename = canConfigCs(user);

  useEffect(() => {
    api().get('/capex-sales/section-order').then((r) => setOrder(r.data.order || [])).catch(() => setOrder([]));
    api().get('/capex-sales/fields', { params: { object: 'account' } }).then((r) => setFields(r.data.fields || [])).catch(() => setFields([]));
  }, [api]);

  const secFields = useCallback((s) => fields.filter((f) => (f.section || 'Autres') === s), [fields]);
  const moveSection = (i, d) => { setOrder((o) => { const a = [...o]; const j = i + d; if (j < 0 || j >= a.length) return a; [a[i], a[j]] = [a[j], a[i]]; return a; }); };
  const moveField = (s, i, d) => {
    const sub = secFields(s); const j = i + d;
    if (j < 0 || j >= sub.length) return;
    const idA = sub[i].id, idB = sub[j].id;
    setFields((prev) => { const arr = [...prev]; const ia = arr.findIndex((x) => x.id === idA); const ib = arr.findIndex((x) => x.id === idB); [arr[ia], arr[ib]] = [arr[ib], arr[ia]]; return arr; });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api().put('/capex-sales/section-order', { order });
      const orderedIds = [];
      order.forEach((s) => secFields(s).forEach((f) => orderedIds.push(f.id)));
      fields.forEach((f) => { if (!orderedIds.includes(f.id)) orderedIds.push(f.id); });
      await api().post('/capex-sales/fields/reorder', { ordered_ids: orderedIds });
      toast.success('Ordre enregistré — appliqué à toutes les fiches');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };

  const startRename = (s) => { setRenaming(s); setRenameVal(s); };
  const doRename = async () => {
    const nv = renameVal.trim();
    if (!nv || nv === renaming) { setRenaming(null); return; }
    try {
      const r = await api().post('/capex-sales/rename-section', { old: renaming, new: nv, object: 'account' });
      setFields((prev) => prev.map((f) => ((f.section || 'Autres') === renaming ? { ...f, section: nv } : f)));
      setOrder(r.data.order || order.map((s) => (s === renaming ? nv : s)));
      if (expanded === renaming) setExpanded(nv);
      toast.success('Rubrique renommée');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); } finally { setRenaming(null); }
  };

  return (
    <div data-testid="cs-rubriques">
      <CxBack label="Retour aux paramètres" onClick={onBack} testid="cs-rubriques-back" />
      <h1 className="cx-h1 mb-1">Ordre des rubriques &amp; des champs</h1>
      <p className="text-xs cx-muted mb-4">Réorganisez les blocs (↑/↓), dépliez un bloc pour réordonner ses champs, et renommez les blocs. Appliqué à toutes les fiches.</p>
      {order === null ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: 'var(--accent)' }} /></div> : (
        <>
          <div className="space-y-2">{order.map((s, i) => {
            const sub = secFields(s);
            const hasFields = sub.length > 0;
            const isOpen = expanded === s;
            const sys = s === 'Account Information';
            return (
              <div key={s} className="cx-surface" data-testid={`cs-rubrique-${i}`} style={{ overflow: 'hidden' }}>
                <div className="flex items-center gap-2 p-3">
                  <span className="cx-pct" style={{ color: 'var(--accent)', borderColor: 'var(--line-2)', fontSize: 13, width: 34, height: 34, flexShrink: 0 }}>{i + 1}</span>
                  {renaming === s ? (
                    <>
                      <input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') doRename(); if (e.key === 'Escape') setRenaming(null); }}
                        className="flex-1 min-w-0" style={{ background: 'var(--tile)', border: '1px solid var(--accent)', color: 'var(--ink)', borderRadius: 8, padding: '5px 9px', fontSize: 13 }} data-testid={`cs-rubrique-rename-input-${i}`} />
                      <button className="cx-stepbtn" style={{ color: 'var(--green)' }} onClick={doRename} data-testid={`cs-rubrique-rename-save-${i}`}><Check className="w-4 h-4" /></button>
                      <button className="cx-stepbtn" onClick={() => setRenaming(null)} data-testid={`cs-rubrique-rename-cancel-${i}`}><X className="w-4 h-4" /></button>
                    </>
                  ) : (
                    <>
                      <button className="flex-1 min-w-0 text-left flex items-center gap-2" onClick={() => hasFields && setExpanded(isOpen ? null : s)} data-testid={`cs-rubrique-toggle-${i}`} disabled={!hasFields}>
                        <span className="font-semibold text-sm truncate" style={{ color: 'var(--ink)' }}>{s}</span>
                        <span className="text-xs cx-muted-3 flex-shrink-0">{sys ? '(bloc système)' : `${sub.length} champ${sub.length > 1 ? 's' : ''}`}</span>
                        {hasFields && <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--ink-3)' }} />}
                      </button>
                      {canRename && !sys && <button className="cx-stepbtn" style={{ color: 'var(--accent)' }} onClick={() => startRename(s)} data-testid={`cs-rubrique-rename-${i}`}><Pencil className="w-3.5 h-3.5" /></button>}
                      <button className="cx-stepbtn" onClick={() => moveSection(i, -1)} disabled={i === 0} data-testid={`cs-rubrique-up-${i}`}>↑</button>
                      <button className="cx-stepbtn" onClick={() => moveSection(i, 1)} disabled={i === order.length - 1} data-testid={`cs-rubrique-down-${i}`}>↓</button>
                    </>
                  )}
                </div>
                {isOpen && hasFields && (
                  <div className="px-3 pb-3 space-y-1.5" style={{ borderTop: '1px solid var(--line)' }} data-testid={`cs-rubrique-fields-${i}`}>
                    {sub.map((f, fi) => (
                      <div key={f.id} className="flex items-center gap-2 mt-1.5 px-2.5 py-2 rounded-lg" style={{ background: 'var(--tile)' }} data-testid={`cs-field-${f.id}`}>
                        <span className="text-xs cx-muted-3 flex-shrink-0" style={{ width: 20 }}>{fi + 1}.</span>
                        <span className="flex-1 min-w-0 text-sm truncate" style={{ color: 'var(--ink)' }}>{f.label}</span>
                        <button className="cx-stepbtn" onClick={() => moveField(s, fi, -1)} disabled={fi === 0} data-testid={`cs-field-up-${f.id}`}>↑</button>
                        <button className="cx-stepbtn" onClick={() => moveField(s, fi, 1)} disabled={fi === sub.length - 1} data-testid={`cs-field-down-${f.id}`}>↓</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}</div>
          <button className="cx-actionbtn primary mt-3" onClick={save} disabled={saving} data-testid="cs-rubriques-save">{saving ? 'Enregistrement…' : "Enregistrer l'ordre"}</button>
        </>
      )}
    </div>
  );
}
